/**
 * Discovery directory (ADR-71, migration 0097): wire shapes and the SIXTH route
 * table, DISCOVERY_ROUTES.
 *
 * The directory is HipNumber's own public record of auctions and of the
 * companies that hold them, the data behind lotnumber.com and the HipNumber
 * discovery pages. A directory entry is ABOUT an auction; it is never the
 * auction. Nothing in this module names a sale, a lot, a registration, a bid or
 * a payment, and the server never turns an external listing into one.
 *
 * Why its own table and not `routes` / `PLATFORM_ROUTES` / `KNOWLEDGE_ROUTES`:
 *
 *  - Not in `routes`: those live under /t/:tenantSlug. A directory entry
 *    belongs to no tenant (a tenant's own sale may be POINTED AT, which is a
 *    different thing).
 *  - Not in `PLATFORM_ROUTES`: that table administers tenants and is never
 *    derived into MCP tools. The directory's writes are a review desk, not
 *    tenant administration, and its reads will be public (cut 2).
 *  - Not in `KNOWLEDGE_ROUTES`: that table's `access` vocabulary is
 *    reader/operator. This one's is public/operator, and the two must never be
 *    confused: a public route serves anonymous visitors from a cache; an
 *    operator route serves the review desk with provenance and `no-store`.
 *
 * Cut 1 declares the operator desk: sources (with their permission status),
 * the manual importer, the review reads and the status moves. Cut 2a adds the
 * three public reads under /discovery/public/ with `access: 'public'` and
 * their own shapes (the Public* schemas below): PUBLISHED-only, by slug only,
 * no provenance, no ids, no contact fields, no full outbound address. Cut 3
 * adds the /out/:linkId redirect (see below). Cut 4a adds the opt-in alert
 * routes: four anonymous writes under /discovery/alerts/ with
 * `access: 'visitor'` (signup, confirm, unsubscribe, preferences) and two
 * operator routes (the subscriber report and the do-not-mail entry). The
 * event collector and the digests are later cuts.
 *
 * Rights. Every record carries the source it came from, and every source
 * carries `permissionStatus`. The importer refuses a source that is not
 * PERMITTED, and publishing refuses an auction whose source is no longer
 * PERMITTED. "Publicly reachable" is not "licensed"; the basis is written on
 * the source row in words.
 *
 * Outbound targets. A company website or an auction's official page is stored
 * once, validated by `safeOutboundUrl`, and addressed by an opaque link id.
 * The redirect (cut 3) resolves ids only; there is no `?url=` anywhere.
 */

import { z } from 'zod';
import { PRODUCT_KEYS } from '@hipnumber/products';

// -- vocabularies ------------------------------------------------------------

/** The brand directories an entry can appear in: the product registry's keys, never restated. */
export const DISCOVERY_BRANDS = PRODUCT_KEYS;
export const DiscoveryBrand = z.enum(DISCOVERY_BRANDS);
export type DiscoveryBrand = z.infer<typeof DiscoveryBrand>;

/** How records from a source are permitted to arrive. */
export const DISCOVERY_SOURCE_KINDS = ['MANUAL', 'PUSH', 'FEED', 'API', 'CRAWL_ALLOWED'] as const;
export const DiscoverySourceKind = z.enum(DISCOVERY_SOURCE_KINDS);
export type DiscoverySourceKind = z.infer<typeof DiscoverySourceKind>;

/** Whether HipNumber may use a source. Only PERMITTED imports or publishes. */
export const DISCOVERY_PERMISSION_STATUSES = ['UNVERIFIED', 'PERMITTED', 'DENIED', 'REVOKED'] as const;
export const DiscoveryPermissionStatus = z.enum(DISCOVERY_PERMISSION_STATUSES);
export type DiscoveryPermissionStatus = z.infer<typeof DiscoveryPermissionStatus>;

/**
 * What the image pipeline may do with the images a source lists (ADR-71 cut 6, plan section 17). Decided once per
 * SOURCE, never per image. A new source is RESTRICTED until an operator classes it.
 */
export const DISCOVERY_SOURCE_CLASSES = [
  'FIRST_PARTY', 'LICENSED_PARTNER', 'AUTHORIZED_FEED', 'PUBLIC_DISCOVERY', 'RESTRICTED', 'TAKEDOWN_BLOCKED',
] as const;
export const DiscoverySourceClass = z.enum(DISCOVERY_SOURCE_CLASSES);
export type DiscoverySourceClass = z.infer<typeof DiscoverySourceClass>;

/** LOGO is a company's mark; PHOTO is a picture of an auction (later, a lot). */
export const DISCOVERY_IMAGE_ROLES = ['LOGO', 'PHOTO'] as const;
export const DiscoveryImageRole = z.enum(DISCOVERY_IMAGE_ROLES);
export type DiscoveryImageRole = z.infer<typeof DiscoveryImageRole>;

/** Where an image is in our storage. Whether it is SHOWN is derived on read (see discoveryImagePolicy), never stored. */
export const DISCOVERY_IMAGE_STATUSES = ['PENDING', 'STORED', 'FAILED', 'SOURCE_GONE', 'REMOVED'] as const;
export const DiscoveryImageStatus = z.enum(DISCOVERY_IMAGE_STATUSES);
export type DiscoveryImageStatus = z.infer<typeof DiscoveryImageStatus>;

/** What a public page may show of a stored image: every size, only the small ones (previews), or nothing. */
export type DiscoveryImageShow = 'FULL' | 'PREVIEW' | 'NONE';

export interface DiscoveryImagePolicy {
  /** Fetch the bytes into our storage at all. RESTRICTED and TAKEDOWN_BLOCKED never do. */
  readonly acquire: boolean;
  readonly show: DiscoveryImageShow;
}

/**
 * The one rule that turns a source's class into what the pipeline and the pages may do. A PUBLIC_DISCOVERY source
 * is acquired and staged always, and shown as previews only once its usage-policy reference is recorded. Pure:
 * the pages ask it on every read, so changing a class or recording a reference takes effect at once.
 */
export function discoveryImagePolicy(sourceClass: DiscoverySourceClass, imagePolicyRef: string | null | undefined): DiscoveryImagePolicy {
  switch (sourceClass) {
    case 'FIRST_PARTY':
    case 'LICENSED_PARTNER':
    case 'AUTHORIZED_FEED':
      return { acquire: true, show: 'FULL' };
    case 'PUBLIC_DISCOVERY':
      return { acquire: true, show: (imagePolicyRef ?? '').trim().length > 0 ? 'PREVIEW' : 'NONE' };
    case 'RESTRICTED':
    case 'TAKEDOWN_BLOCKED':
      return { acquire: false, show: 'NONE' };
  }
}

export const DISCOVERY_COMPANY_STATUSES = ['PENDING_REVIEW', 'PUBLISHED', 'HIDDEN', 'RETRACTED'] as const;
export const DiscoveryCompanyStatus = z.enum(DISCOVERY_COMPANY_STATUSES);
export type DiscoveryCompanyStatus = z.infer<typeof DiscoveryCompanyStatus>;

export const DISCOVERY_CLAIM_STATUSES = ['UNCLAIMED', 'CLAIM_PENDING', 'CLAIMED'] as const;
export const DiscoveryClaimStatus = z.enum(DISCOVERY_CLAIM_STATUSES);
export type DiscoveryClaimStatus = z.infer<typeof DiscoveryClaimStatus>;

/**
 * The auction lifecycle. The happy path is DISCOVERED -> PENDING_REVIEW ->
 * VERIFIED -> PUBLISHED -> ENDED -> ARCHIVED. QUARANTINED, RETRACTED,
 * SOURCE_BLOCKED and STALE are exits a reviewer or the system takes. Only
 * PUBLISHED is ever shown to the public.
 */
export const DISCOVERY_AUCTION_STATUSES = [
  'DISCOVERED', 'PENDING_REVIEW', 'VERIFIED', 'PUBLISHED', 'ENDED', 'ARCHIVED',
  'QUARANTINED', 'RETRACTED', 'SOURCE_BLOCKED', 'STALE',
] as const;
export const DiscoveryAuctionStatus = z.enum(DISCOVERY_AUCTION_STATUSES);
export type DiscoveryAuctionStatus = z.infer<typeof DiscoveryAuctionStatus>;

export const DISCOVERY_AUCTION_FORMATS = ['LIVE', 'ONLINE', 'HYBRID', 'UNKNOWN'] as const;
export const DiscoveryAuctionFormat = z.enum(DISCOVERY_AUCTION_FORMATS);
export type DiscoveryAuctionFormat = z.infer<typeof DiscoveryAuctionFormat>;

/** LOT_BIDDING (cut 8a): the one page where a visitor goes to bid on a lot; it belongs to the lot, never to the auction's own set. */
export const DISCOVERY_LINK_PURPOSES = ['AUCTION_OFFICIAL', 'AUCTION_CATALOG', 'AUCTION_BIDDING', 'COMPANY_WEBSITE', 'LOT_BIDDING'] as const;
export const DiscoveryLinkPurpose = z.enum(DISCOVERY_LINK_PURPOSES);
export type DiscoveryLinkPurpose = z.infer<typeof DiscoveryLinkPurpose>;

/**
 * Which status moves a reviewer may make, from -> to. Lives in code so a new
 * rule is a one-line change and the route test can assert the shape: the two
 * terminal states (ARCHIVED, RETRACTED) have no exits, and nothing reaches
 * PUBLISHED except from VERIFIED or back from STALE. The server applies two
 * more checks on the move to PUBLISHED that need the database: the company
 * must be PUBLISHED and the source still PERMITTED.
 */
export const DISCOVERY_AUCTION_TRANSITIONS: Readonly<Record<DiscoveryAuctionStatus, readonly DiscoveryAuctionStatus[]>> = {
  DISCOVERED: ['PENDING_REVIEW', 'QUARANTINED', 'SOURCE_BLOCKED'],
  PENDING_REVIEW: ['VERIFIED', 'QUARANTINED', 'RETRACTED', 'SOURCE_BLOCKED'],
  VERIFIED: ['PUBLISHED', 'PENDING_REVIEW', 'QUARANTINED', 'RETRACTED', 'SOURCE_BLOCKED'],
  PUBLISHED: ['ENDED', 'STALE', 'RETRACTED', 'QUARANTINED', 'SOURCE_BLOCKED'],
  ENDED: ['ARCHIVED', 'RETRACTED'],
  STALE: ['PUBLISHED', 'ENDED', 'RETRACTED', 'ARCHIVED', 'SOURCE_BLOCKED'],
  QUARANTINED: ['PENDING_REVIEW', 'RETRACTED', 'SOURCE_BLOCKED'],
  SOURCE_BLOCKED: ['PENDING_REVIEW', 'RETRACTED'],
  ARCHIVED: [],
  RETRACTED: [],
};

/** Company moves. HIDDEN keeps the record off the public pages without retracting it. */
export const DISCOVERY_COMPANY_TRANSITIONS: Readonly<Record<DiscoveryCompanyStatus, readonly DiscoveryCompanyStatus[]>> = {
  PENDING_REVIEW: ['PUBLISHED', 'HIDDEN', 'RETRACTED'],
  PUBLISHED: ['HIDDEN', 'RETRACTED'],
  HIDDEN: ['PUBLISHED', 'RETRACTED'],
  RETRACTED: [],
};

export const canMoveDiscoveryAuction = (from: DiscoveryAuctionStatus, to: DiscoveryAuctionStatus): boolean =>
  DISCOVERY_AUCTION_TRANSITIONS[from].includes(to);
export const canMoveDiscoveryCompany = (from: DiscoveryCompanyStatus, to: DiscoveryCompanyStatus): boolean =>
  DISCOVERY_COMPANY_TRANSITIONS[from].includes(to);

// -- slugs -------------------------------------------------------------------

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** True when a path segment is an id rather than a slug (a slug may never look like one). */
export const looksLikeDiscoveryId = (segment: string): boolean => UUID_SHAPE.test(segment);

/** Lowercase words joined by single hyphens; never shaped like an id, so `:slug` can also take an id. */
export const DiscoverySlug = z
  .string()
  .min(1)
  .max(120)
  .regex(SLUG, 'use lowercase letters, digits and single hyphens')
  .refine((s) => !UUID_SHAPE.test(s), 'a slug cannot look like an id');

/** The key a source is addressed by: same shape as a slug, shorter. */
export const DiscoverySourceKey = z.string().min(2).max(60).regex(SLUG, 'use lowercase letters, digits and single hyphens');

/** "Griffin Auction Service, Inc." -> "griffin-auction-service-inc". Never empty, never id-shaped. */
export function discoverySlugFromName(name: string, fallback = 'entry'): string {
  const words = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 100)
    .replace(/-+$/, '');
  return words && !UUID_SHAPE.test(words) ? words : fallback;
}

/** An auction's public address carries its date, so "Fall Mule Sale" in two years is two slugs. */
export function discoveryAuctionSlug(title: string, startsAt: string | Date): string {
  const d = new Date(startsAt);
  const day = Number.isNaN(d.getTime()) ? '' : `-${d.toISOString().slice(0, 10)}`;
  return `${discoverySlugFromName(title, 'auction').slice(0, 100 - day.length).replace(/-+$/, '')}${day}`;
}

// -- outbound targets --------------------------------------------------------

export type SafeOutboundUrl = { ok: true; url: string } | { ok: false; reason: string };

const PRIVATE_SUFFIXES = ['.localhost', '.local', '.internal', '.intranet', '.lan', '.home', '.corp', '.arpa', '.onion', '.invalid'];
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * The one rule for where a directory link may point. A browser redirect, not a
 * server fetch, so the worry is an open redirect and a link into somebody's
 * intranet, not SSRF; the rule is still the strict one:
 *
 *   - http or https only (never javascript:, data:, file:, ftp:, mailto:)
 *   - no credentials in the address
 *   - the default port only
 *   - a real public host name: at least one dot, letters in the last label,
 *     not an IP address, not a private or reserved suffix
 *   - at most 2000 characters
 *
 * Returns the normalised address (the URL parser's serialisation) so what is
 * stored is what is compared.
 */
export function safeOutboundUrl(input: string): SafeOutboundUrl {
  const raw = input.trim();
  if (raw.length === 0) return { ok: false, reason: 'empty' };
  if (raw.length > 2000) return { ok: false, reason: 'longer than 2000 characters' };
  let u: URL;
  try { u = new URL(raw); } catch { return { ok: false, reason: 'not an absolute address' }; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return { ok: false, reason: `scheme ${u.protocol.replace(/:$/, '')} is not http or https` };
  if (u.username || u.password) return { ok: false, reason: 'credentials in the address' };
  if (u.port !== '') return { ok: false, reason: 'a port other than the default' };
  const host = u.hostname.toLowerCase();
  if (host.startsWith('[') || host.includes(':')) return { ok: false, reason: 'an IP address, not a host name' };
  if (IPV4.test(host)) return { ok: false, reason: 'an IP address, not a host name' };
  if (!host.includes('.')) return { ok: false, reason: 'not a public host name' };
  if (host.endsWith('.')) return { ok: false, reason: 'not a public host name' };
  if (PRIVATE_SUFFIXES.some((s) => host.endsWith(s))) return { ok: false, reason: 'a private or reserved host' };
  const last = host.slice(host.lastIndexOf('.') + 1);
  if (!/^[a-z][a-z0-9-]*$/.test(last) && !last.startsWith('xn--')) return { ok: false, reason: 'not a public host name' };
  return { ok: true, url: u.toString() };
}

/** A string that `safeOutboundUrl` accepts; the server stores the normalised form. */
export const DiscoveryTargetUrl = z.string().trim().min(1).max(2000).refine((s) => safeOutboundUrl(s).ok, {
  message: 'must be a public http(s) address with no credentials, port or private host',
});

// -- wire shapes -------------------------------------------------------------

const Iso = z.string();
const Category = z.string().regex(/^[a-z][a-z0-9-]{1,39}$/, 'a lowercase word like horse, mule, cattle or equipment');
const Country = z.string().regex(/^[A-Z]{2}$/, 'two upper-case letters (ISO 3166-1)');
const Place = {
  city: z.string().trim().max(120).nullable(),
  region: z.string().trim().max(120).nullable(),
  country: Country,
  postalCode: z.string().trim().max(20).nullable(),
};

export const DiscoverySource = z.object({
  id: z.string().uuid(),
  sourceKey: z.string(),
  displayName: z.string(),
  kind: DiscoverySourceKind,
  permissionStatus: DiscoveryPermissionStatus,
  permissionBasis: z.string(),
  permissionEvidence: z.record(z.unknown()),
  termsUrl: z.string().nullable(),
  contact: z.string().nullable(),
  notes: z.string(),
  /** What the image pipeline may do with this source's images. */
  sourceClass: DiscoverySourceClass,
  /** The recorded usage-policy reference that lets a PUBLIC_DISCOVERY source show previews. */
  imagePolicyRef: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: Iso,
  updatedAt: Iso,
  /** How many directory records trace to this source. */
  counts: z.object({ companies: z.number().int().nonnegative(), auctions: z.number().int().nonnegative() }),
});
export type DiscoverySource = z.infer<typeof DiscoverySource>;

export const DiscoveryOutboundLink = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  auctionId: z.string().uuid().nullable(),
  /** Set exactly for LOT_BIDDING (cut 8a). */
  lotId: z.string().uuid().nullable().default(null),
  purpose: DiscoveryLinkPurpose,
  targetUrl: z.string(),
  status: z.enum(['ACTIVE', 'DISABLED']),
});
export type DiscoveryOutboundLink = z.infer<typeof DiscoveryOutboundLink>;

/** Where a record came from, for the review desk. Never on a public shape. */
export const DiscoveryProvenance = z.object({
  sourceKey: z.string(),
  externalId: z.string(),
  firstSeenAt: Iso,
  lastSeenAt: Iso,
});
export type DiscoveryProvenance = z.infer<typeof DiscoveryProvenance>;

export const DiscoveryCompany = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  displayName: z.string(),
  legalName: z.string().nullable(),
  description: z.string(),
  websiteUrl: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  ...Place,
  categories: z.array(z.string()),
  brands: z.array(z.string()),
  status: DiscoveryCompanyStatus,
  statusReason: z.string(),
  statusChangedAt: Iso,
  claimStatus: DiscoveryClaimStatus,
  firstPartyTenantId: z.string().uuid().nullable(),
  publishedAt: Iso.nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: Iso,
  updatedAt: Iso,
  sources: z.array(DiscoveryProvenance),
  links: z.array(DiscoveryOutboundLink),
  auctionCount: z.number().int().nonnegative(),
});
export type DiscoveryCompany = z.infer<typeof DiscoveryCompany>;

/** One page an agent saw an auction on. `corroborates`: the auction was already here from another source; this is a second witness. */
export const DiscoveryEvidenceRow = z.object({
  agentKey: z.string(),
  pageUrl: z.string(),
  observedAt: z.string(),
  excerpt: z.string(),
  corroborates: z.boolean(),
  lastSeenAt: z.string(),
});
export type DiscoveryEvidenceRow = z.infer<typeof DiscoveryEvidenceRow>;

export const DiscoveryAuction = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  companyId: z.string().uuid(),
  companySlug: z.string(),
  companyName: z.string(),
  title: z.string(),
  description: z.string(),
  format: DiscoveryAuctionFormat,
  startsAt: Iso,
  endsAt: Iso.nullable(),
  timeZone: z.string(),
  venueName: z.string().nullable(),
  ...Place,
  categories: z.array(z.string()),
  brands: z.array(z.string()),
  status: DiscoveryAuctionStatus,
  statusReason: z.string(),
  statusChangedAt: Iso,
  firstPartySaleId: z.string().uuid().nullable(),
  publishedAt: Iso.nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: Iso,
  updatedAt: Iso,
  sources: z.array(DiscoveryProvenance),
  links: z.array(DiscoveryOutboundLink),
  /** The pages agents saw it on (cut 7). Empty for a record no agent has seen. */
  evidence: z.array(DiscoveryEvidenceRow).default([]),
});
export type DiscoveryAuction = z.infer<typeof DiscoveryAuction>;

/** One page, whole records only, and how many more there are (engineering rule 4). */
const page = <T extends z.ZodTypeAny>(item: T) => z.object({
  items: z.array(item),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
});
export const DiscoveryCompanyList = page(DiscoveryCompany);
export type DiscoveryCompanyList = z.infer<typeof DiscoveryCompanyList>;
export const DiscoveryAuctionList = page(DiscoveryAuction);
export type DiscoveryAuctionList = z.infer<typeof DiscoveryAuctionList>;

// -- cut 2b: how the public pages address a record and judge whether it earns a page ----

/** The handle in /auctions/{slug}-{short-id}/: 10 lowercase hex characters, drawn once by the database (migration 0100). */
export const DISCOVERY_SHORT_ID_SHAPE = /^[0-9a-f]{10}$/;
export const DiscoveryShortId = z.string().regex(DISCOVERY_SHORT_ID_SHAPE, '10 lowercase hex characters');

/** An auction's public path segment: the slug is wording, the short id is the address. */
export const discoveryAuctionSegment = (slug: string, shortId: string): string => `${slug}-${shortId}`;

/** Take a path segment apart. Null unless it is `{slug}-{short-id}` with a well-formed slug and handle. */
export function parseDiscoveryAuctionSegment(segment: string): { slug: string; shortId: string } | null {
  const m = /^(.+)-([0-9a-f]{10})$/.exec(segment);
  if (!m) return null;
  const slug = m[1]!;
  return DiscoverySlug.safeParse(slug).success ? { slug, shortId: m[2]! } : null;
}

/**
 * Below this many characters (after trimming) a description does not count as
 * words of the record's own. The SQL of the sitemap feed and the pure rules
 * below use the same number, and one suite holds them to the same answer.
 */
export const DISCOVERY_MIN_DESCRIPTION = 60;
const hasOwnWords = (s: string): boolean => s.trim().length >= DISCOVERY_MIN_DESCRIPTION;

/**
 * A company page is worth indexing when it says where the company is and has
 * either words of its own or at least one public auction in the brand. A
 * name and a town alone is a row in a list, not a page; it is still served
 * (a visitor can reach it and follow its link) but it asks search engines to
 * stay away and stays out of the sitemap.
 */
export const isIndexableDiscoveryCompany = (c: { region: string | null; description: string; auctionCount: number }): boolean =>
  c.region !== null && (hasOwnWords(c.description) || c.auctionCount > 0);

/**
 * An auction page is worth indexing when it says where the sale is and gives
 * the visitor a way onward (an active link) or words of its own.
 */
export const isIndexableDiscoveryAuction = (a: { city: string | null; region: string | null; description: string; links: readonly unknown[] }): boolean =>
  (a.city !== null || a.region !== null) && (a.links.length > 0 || hasOwnWords(a.description));

// -- public shapes (cut 2a) --------------------------------------------------
//
// What an anonymous visitor sees. These are separate schemas, not views of the
// operator shapes, so what is public is public by construction: no id, no
// status, no provenance, no raw record, no operator name, no first-party id,
// no contact field, no postal code, and no outbound address. A public record
// is addressed by its slug; a link by its opaque id plus the host it goes to.
// The server decides what is public (PUBLISHED, company PUBLISHED, every source
// still PERMITTED, the brand asked for); the shapes cannot say otherwise.

/** A link as the public sees it: hand `id` to /out/:linkId (cut 3); show `host`. */
export const PublicDiscoveryLink = z.object({
  id: z.string().uuid(),
  purpose: DiscoveryLinkPurpose,
  /** The target's host name, for display ("example.com"). The address stays behind the id. */
  host: z.string(),
});
export type PublicDiscoveryLink = z.infer<typeof PublicDiscoveryLink>;

const PublicPlace = {
  city: z.string().nullable(),
  region: z.string().nullable(),
  country: Country,
};

/**
 * An image a public page may show (ADR-71 cut 6d). Only ever present for a STORED image whose source is still
 * PERMITTED, whose class lets a page show it (discoveryImagePolicy), and whose bytes and address are not blocked,
 * decided on every read. Every address is on the media host of the brand the request names; with no host
 * configured for that brand there are no images, never a wrong-host address. No id, no source, no provenance.
 *
 * The sizes a page may use depend on the source's class: a licensed or first-party image has all of them, a
 * PUBLIC_DISCOVERY preview has only the small ones (`gallery` is null), so a page can never stand a preview in for
 * the photo.
 */
export const PublicDiscoveryImage = z.object({
  role: DiscoveryImageRole,
  /** 320 x 240, WebP, cropped. */
  thumb: z.string().url(),
  /** 640 x 480, WebP, cropped. */
  card: z.string().url(),
  /** The card in JPEG, for a client without WebP. */
  cardJpg: z.string().url().nullable(),
  /** 1280 wide, WebP, uncropped. Null for a preview and for an image that was stored before its source was licensed. */
  gallery: z.string().url().nullable(),
  /** The stored original's size, for a page to reserve room. */
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export type PublicDiscoveryImage = z.infer<typeof PublicDiscoveryImage>;

/** The company line on an auction. */
export const PublicDiscoveryCompanyCard = z.object({
  slug: z.string(),
  displayName: z.string(),
  ...PublicPlace,
  /** The company's logo, when it has one a page may show. */
  logo: PublicDiscoveryImage.nullable().default(null),
  /** True when the company is a HipNumber or LotNumber customer (its own sales are first-party). */
  firstParty: z.boolean(),
});
export type PublicDiscoveryCompanyCard = z.infer<typeof PublicDiscoveryCompanyCard>;

export const PublicDiscoveryAuction = z.object({
  slug: z.string(),
  /** The immutable handle in /auctions/{slug}-{short-id}/ (cut 2b, migration 0100). The slug is wording; this is the address. */
  shortId: DiscoveryShortId,
  title: z.string(),
  description: z.string(),
  format: DiscoveryAuctionFormat,
  startsAt: Iso,
  endsAt: Iso.nullable(),
  timeZone: z.string(),
  venueName: z.string().nullable(),
  ...PublicPlace,
  categories: z.array(z.string()),
  brands: z.array(z.string()),
  publishedAt: Iso,
  /** When the record last changed; a sitemap's lastmod (cut 2b). */
  updatedAt: Iso,
  /** True when this auction is a HipNumber or LotNumber tenant's own sale. The sale itself is not here. */
  firstParty: z.boolean(),
  company: PublicDiscoveryCompanyCard,
  /** The auction's pictures a page may show, the first being the cover. A list answer carries the cover only; one auction carries them all (DISCOVERY_MAX_IMAGES_PER_RECORD). */
  images: z.array(PublicDiscoveryImage).default([]),
  /** ACTIVE links only, by purpose. */
  links: z.array(PublicDiscoveryLink),
});
export type PublicDiscoveryAuction = z.infer<typeof PublicDiscoveryAuction>;

export const PublicDiscoveryCompany = z.object({
  slug: z.string(),
  displayName: z.string(),
  description: z.string(),
  ...PublicPlace,
  categories: z.array(z.string()),
  brands: z.array(z.string()),
  publishedAt: Iso,
  updatedAt: Iso,
  firstParty: z.boolean(),
  /** The company's logo, when it has one a page may show. */
  logo: PublicDiscoveryImage.nullable().default(null),
  /** The company's website, as an opaque link, when it has one. */
  website: PublicDiscoveryLink.nullable(),
  /** Its public auctions that have not ended, soonest first, at most DISCOVERY_PUBLIC_MAX_LIMIT. */
  upcoming: z.array(PublicDiscoveryAuction),
  /** How many public auctions it has in all, past ones included. */
  auctionCount: z.number().int().nonnegative(),
});
export type PublicDiscoveryCompany = z.infer<typeof PublicDiscoveryCompany>;

export const PublicDiscoveryAuctionList = page(PublicDiscoveryAuction);
export type PublicDiscoveryAuctionList = z.infer<typeof PublicDiscoveryAuctionList>;

/** One company in a list: enough for a directory row, nothing that needs a second read. */
export const PublicDiscoveryCompanyListItem = z.object({
  slug: z.string(),
  displayName: z.string(),
  ...PublicPlace,
  categories: z.array(z.string()),
  firstParty: z.boolean(),
  /** The company's logo, when it has one a page may show. */
  logo: PublicDiscoveryImage.nullable().default(null),
  /** Its public auctions in the brand asked for that have not ended. */
  upcoming: z.number().int().nonnegative(),
});
export type PublicDiscoveryCompanyListItem = z.infer<typeof PublicDiscoveryCompanyListItem>;

export const PublicDiscoveryCompanyList = page(PublicDiscoveryCompanyListItem);
export type PublicDiscoveryCompanyList = z.infer<typeof PublicDiscoveryCompanyList>;

/** The body of the 301 on a company slug it has left: where it lives now. */
export const PublicDiscoveryMoved = z.object({ error: z.literal('moved'), slug: z.string() });
export type PublicDiscoveryMoved = z.infer<typeof PublicDiscoveryMoved>;

/**
 * What there is to browse by, with counts, so a page can decline to exist for
 * a place or a category with nothing in it. Companies are those the brand's
 * directory lists; `upcoming` counts public auctions that have not ended.
 */
export const PublicDiscoveryFacets = z.object({
  regions: z.array(z.object({ country: Country, region: z.string(), companies: z.number().int().nonnegative(), upcoming: z.number().int().nonnegative() })),
  categories: z.array(z.object({ category: z.string(), companies: z.number().int().nonnegative(), upcoming: z.number().int().nonnegative() })),
});
export type PublicDiscoveryFacets = z.infer<typeof PublicDiscoveryFacets>;

/** One sitemap row: the address parts and when the record last changed. Auctions carry a short id, companies do not. */
export const PublicDiscoverySitemapItem = z.object({ slug: z.string(), shortId: DiscoveryShortId.nullable(), updatedAt: Iso });
export type PublicDiscoverySitemapItem = z.infer<typeof PublicDiscoverySitemapItem>;

export const PublicDiscoverySitemap = page(PublicDiscoverySitemapItem);
export type PublicDiscoverySitemap = z.infer<typeof PublicDiscoverySitemap>;

// -- requests ----------------------------------------------------------------

const Name = z.string().trim().min(1).max(200);
const Text = z.string().trim().max(5000);
const Short = z.string().trim().max(200);

/** Create or replace a source. The permission fields are the point: say why we may use it. */
export const UpsertDiscoverySourceBody = z.object({
  displayName: Name,
  kind: DiscoverySourceKind.optional(),
  permissionStatus: DiscoveryPermissionStatus.optional(),
  /** Required when permissionStatus is PERMITTED: the basis, in words. */
  permissionBasis: z.string().trim().max(1000).optional(),
  permissionEvidence: z.record(z.unknown()).optional(),
  termsUrl: DiscoveryTargetUrl.nullable().optional(),
  contact: Short.nullable().optional(),
  notes: Text.optional(),
  /** What the image pipeline may do with this source's images. Left alone when absent. */
  sourceClass: DiscoverySourceClass.optional(),
  /** The usage-policy reference for a PUBLIC_DISCOVERY source; null clears it. */
  imagePolicyRef: z.string().trim().min(1).max(500).nullable().optional(),
}).refine((b) => b.permissionStatus !== 'PERMITTED' || (b.permissionBasis ?? '').length > 0, {
  message: 'a PERMITTED source must say on what basis', path: ['permissionBasis'],
});
export type UpsertDiscoverySourceBody = z.infer<typeof UpsertDiscoverySourceBody>;

/**
 * One image a source lists for a company (a logo) or an auction (a picture). The directory never hotlinks it: the
 * pipeline fetches it into its own storage when the source's class allows, and pages show our copy.
 */
export const DiscoveryImageInput = z.object({
  url: DiscoveryTargetUrl,
  /** Defaults to LOGO on a company and PHOTO on an auction; a lot's pictures are always PHOTO. */
  role: DiscoveryImageRole.optional(),
  /** The source's page the image was found on; kept as provenance. */
  pageUrl: DiscoveryTargetUrl.optional(),
});
export type DiscoveryImageInput = z.infer<typeof DiscoveryImageInput>;
/** Absent: leave the images as they are. Present (even empty): the source's whole list, so a dropped one is marked gone. */
export const DISCOVERY_MAX_IMAGES_PER_RECORD = 12;

const PlaceBody = {
  city: Short.optional(),
  region: Short.optional(),
  /** Defaults to US. */
  country: Country.optional(),
  postalCode: z.string().trim().max(20).optional(),
};

/** The company half of a manual import. externalId is the source's own id for it. */
export const DiscoveryCompanyInput = z.object({
  externalId: z.string().trim().min(1).max(200),
  displayName: Name,
  legalName: Short.optional(),
  description: Text.optional(),
  websiteUrl: DiscoveryTargetUrl.optional(),
  /** Public business contact only. */
  email: z.string().trim().email().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
  ...PlaceBody,
  categories: z.array(Category).max(12).optional(),
  brands: z.array(DiscoveryBrand).min(1).max(2),
  /** Chosen address; defaults to the display name, slugified. */
  slug: DiscoverySlug.optional(),
  images: z.array(DiscoveryImageInput).max(DISCOVERY_MAX_IMAGES_PER_RECORD).optional(),
});
export type DiscoveryCompanyInput = z.infer<typeof DiscoveryCompanyInput>;

/** The auction half of a manual import. */
export const DiscoveryAuctionInput = z.object({
  externalId: z.string().trim().min(1).max(200),
  title: Name,
  description: Text.optional(),
  format: DiscoveryAuctionFormat.optional(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }).optional(),
  /** IANA zone, e.g. America/Chicago. */
  timeZone: z.string().trim().min(1).max(60),
  venueName: Short.optional(),
  ...PlaceBody,
  categories: z.array(Category).max(12).optional(),
  brands: z.array(DiscoveryBrand).min(1).max(2),
  /** The one place a visitor is sent to for this auction. Required. */
  officialUrl: DiscoveryTargetUrl,
  catalogUrl: DiscoveryTargetUrl.optional(),
  biddingUrl: DiscoveryTargetUrl.optional(),
  slug: DiscoverySlug.optional(),
  images: z.array(DiscoveryImageInput).max(DISCOVERY_MAX_IMAGES_PER_RECORD).optional(),
}).refine((a) => !a.endsAt || new Date(a.endsAt) >= new Date(a.startsAt), { message: 'endsAt is before startsAt', path: ['endsAt'] });
export type DiscoveryAuctionInput = z.infer<typeof DiscoveryAuctionInput>;

/**
 * One company and, when the source has one, one of its auctions, from one
 * source. A company on its own is a complete record (a directory lists
 * companies that hold no auction we know of yet); an auction is never
 * imported without its company. Idempotent on (source, externalId): a second
 * import updates the record and never makes a second one. `dryRun` answers
 * with what would be written and writes nothing.
 */
export const DiscoveryManualImportBody = z.object({
  company: DiscoveryCompanyInput,
  auction: DiscoveryAuctionInput.optional(),
  dryRun: z.boolean().optional(),
});
export type DiscoveryManualImportBody = z.infer<typeof DiscoveryManualImportBody>;

export const DiscoveryImportResult = z.object({
  sourceKey: z.string(),
  dryRun: z.boolean(),
  company: DiscoveryCompany.extend({ created: z.boolean() }),
  /** Present only when the import carried an auction. */
  auction: DiscoveryAuction.extend({ created: z.boolean() }).optional(),
});
export type DiscoveryImportResult = z.infer<typeof DiscoveryImportResult>;

export const SetDiscoveryAuctionStatusBody = z.object({
  status: DiscoveryAuctionStatus,
  reason: z.string().trim().max(1000).optional(),
});
export type SetDiscoveryAuctionStatusBody = z.infer<typeof SetDiscoveryAuctionStatusBody>;

export const SetDiscoveryCompanyStatusBody = z.object({
  status: DiscoveryCompanyStatus,
  reason: z.string().trim().max(1000).optional(),
});
export type SetDiscoveryCompanyStatusBody = z.infer<typeof SetDiscoveryCompanyStatusBody>;

/** The most companies one bulk move touches; a bigger set takes a second round. */
export const DISCOVERY_BULK_STATUS_MAX = 5000;

/**
 * Move many companies of ONE source along DISCOVERY_COMPANY_TRANSITIONS in one
 * call (cut 11): every company that has a record from `sourceKey`, is `from`,
 * and matches the same brand and search the review list takes. `dryRun` counts
 * and writes nothing. Publishing needs the source to be PERMITTED. At most
 * `limit` (default and ceiling DISCOVERY_BULK_STATUS_MAX) move per call, in the
 * list's own order; `remaining` says how many still match for another round.
 */
export const BulkSetDiscoveryCompanyStatusBody = z.object({
  sourceKey: DiscoverySourceKey,
  from: DiscoveryCompanyStatus,
  to: DiscoveryCompanyStatus,
  brand: DiscoveryBrand.optional(),
  search: z.string().trim().max(200).optional(),
  reason: z.string().trim().max(1000).optional(),
  limit: z.number().int().min(1).max(DISCOVERY_BULK_STATUS_MAX).optional(),
  dryRun: z.boolean().optional(),
});
export type BulkSetDiscoveryCompanyStatusBody = z.infer<typeof BulkSetDiscoveryCompanyStatusBody>;

export const DiscoveryBulkStatusResult = z.object({
  sourceKey: DiscoverySourceKey,
  from: DiscoveryCompanyStatus,
  to: DiscoveryCompanyStatus,
  dryRun: z.boolean(),
  /** Every company that matches, before this call moved any. */
  matched: z.number().int().min(0),
  /** Moved by this call (0 on a dry run). */
  changed: z.number().int().min(0),
  /** Still matching after this call: `matched` minus `changed`. */
  remaining: z.number().int().min(0),
});
export type DiscoveryBulkStatusResult = z.infer<typeof DiscoveryBulkStatusResult>;

export const DiscoveryCompanyListQuery = z.object({
  status: DiscoveryCompanyStatus.optional(),
  brand: DiscoveryBrand.optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type DiscoveryCompanyListQuery = z.infer<typeof DiscoveryCompanyListQuery>;

export const DiscoveryAuctionListQuery = z.object({
  status: DiscoveryAuctionStatus.optional(),
  brand: DiscoveryBrand.optional(),
  companyId: z.string().uuid().optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type DiscoveryAuctionListQuery = z.infer<typeof DiscoveryAuctionListQuery>;

/** Page size when `limit` is not sent. */
export const DISCOVERY_DEFAULT_LIMIT = 50;

/** The most a public page may ask for (and the default). */
export const DISCOVERY_PUBLIC_MAX_LIMIT = 50;

/** Which auctions a public list shows by date: not yet ended (default), already ended, or both. */
export const DISCOVERY_PUBLIC_WHEN = ['upcoming', 'past', 'all'] as const;
export const DiscoveryPublicWhen = z.enum(DISCOVERY_PUBLIC_WHEN);
export type DiscoveryPublicWhen = z.infer<typeof DiscoveryPublicWhen>;

/**
 * The public list. `brand` is required: the LotNumber and HipNumber
 * directories are separate surfaces and a request names the one it is for.
 * `q` is matched word by word against title, venue, city, region and company
 * name; `region` is an exact (case-insensitive) match; `category` is one of
 * the record's tokens.
 */
export const PublicDiscoveryAuctionListQuery = z.object({
  brand: DiscoveryBrand,
  q: z.string().trim().max(200).optional(),
  region: z.string().trim().min(1).max(120).optional(),
  country: Country.optional(),
  category: Category.optional(),
  when: DiscoveryPublicWhen.optional(),
  limit: z.coerce.number().int().min(1).max(DISCOVERY_PUBLIC_MAX_LIMIT).optional(),
  offset: z.coerce.number().int().min(0).max(5000).optional(),
});
export type PublicDiscoveryAuctionListQuery = z.infer<typeof PublicDiscoveryAuctionListQuery>;

/**
 * A single public read may name the brand its page is for: the record must
 * then carry that brand (a company's `upcoming` and `auctionCount` are counted
 * within it). Without a brand the record is read as it is.
 */
export const PublicDiscoveryReadQuery = z.object({ brand: DiscoveryBrand.optional() });
export type PublicDiscoveryReadQuery = z.infer<typeof PublicDiscoveryReadQuery>;

/** How deep a company list may page: the seed is about ten thousand companies. */
export const DISCOVERY_COMPANY_LIST_MAX_OFFSET = 20000;

/**
 * The public company list. `brand` is required, like the auction list.
 * `q` is matched word by word against name, city and region; `region` is an
 * exact (case-insensitive) match; `category` is one of the record's tokens.
 * Alphabetical by name.
 */
export const PublicDiscoveryCompanyListQuery = z.object({
  brand: DiscoveryBrand,
  q: z.string().trim().max(200).optional(),
  region: z.string().trim().min(1).max(120).optional(),
  country: Country.optional(),
  category: Category.optional(),
  limit: z.coerce.number().int().min(1).max(DISCOVERY_PUBLIC_MAX_LIMIT).optional(),
  // Deeper than the auction list: the seed is about ten thousand companies, so a national alphabetical list must reach its last page.
  offset: z.coerce.number().int().min(0).max(DISCOVERY_COMPANY_LIST_MAX_OFFSET).optional(),
});
export type PublicDiscoveryCompanyListQuery = z.infer<typeof PublicDiscoveryCompanyListQuery>;

export const PublicDiscoveryFacetsQuery = z.object({ brand: DiscoveryBrand });
export type PublicDiscoveryFacetsQuery = z.infer<typeof PublicDiscoveryFacetsQuery>;

/** What a sitemap feed lists. */
export const DISCOVERY_SITEMAP_KINDS = ['auctions', 'companies', 'lots'] as const;
/** One sitemap file may hold 50,000 addresses; a feed page is far smaller so a read stays quick. */
export const DISCOVERY_SITEMAP_MAX_LIMIT = 5000;
/**
 * The feed a sitemap is built from: only records that earn a page of their own
 * (isIndexableDiscoveryCompany, isIndexableDiscoveryAuction), by brand, in a
 * stable order (slug) so paging is repeatable. `when` applies to auctions only
 * (default `upcoming`: an auction that has ended keeps its page but not its
 * place in the sitemap) and is ignored for companies.
 */
export const PublicDiscoverySitemapQuery = z.object({
  brand: DiscoveryBrand,
  kind: z.enum(DISCOVERY_SITEMAP_KINDS),
  when: DiscoveryPublicWhen.optional(),
  limit: z.coerce.number().int().min(1).max(DISCOVERY_SITEMAP_MAX_LIMIT).optional(),
  offset: z.coerce.number().int().min(0).max(1_000_000).optional(),
});
export type PublicDiscoverySitemapQuery = z.infer<typeof PublicDiscoverySitemapQuery>;

// -- cut 3: the handoff and its record -----------------------------------------

/**
 * Where a public link id goes: `GET /out/:linkId` on the API host answers 302
 * to the address stored under that id, and to nothing else. The id is the only
 * input; a `?url=` or any other address in the query is never read. The path
 * exists with the public reads (DISCOVERY_PUBLIC_ENABLED); the handoff is
 * recorded only while DISCOVERY_COLLECT_ENABLED. Not in DISCOVERY_ROUTES,
 * because its answer is a redirect, not a shape; a page builds the href with
 * `discoveryOutPath`. The optional `?brand=` of PublicDiscoveryReadQuery says
 * which brand surface the page is; it is kept on the record only when the
 * record carries that brand.
 */
export const DISCOVERY_OUT_PATH = '/out/:linkId';
export const discoveryOutPath = (linkId: string): string => `/out/${encodeURIComponent(linkId)}`;

/**
 * The attribution ids a FIRST-PARTY handoff carries, built by
 * packages/marketing-contract/src/attribution.ts (imported, never forked):
 * `hn_campaign` names the directory surface, `hn_asset` is the link id, so a
 * storefront page view can later be joined back to the click with nothing
 * else. A third-party target is sent the stored address untouched: no
 * parameter of ours lands on someone else's site.
 */
export const DISCOVERY_HANDOFF_CAMPAIGN = 'discovery';
export const discoveryHandoffCampaignId = (brand?: DiscoveryBrand): string =>
  brand ? `${DISCOVERY_HANDOFF_CAMPAIGN}-${brand.toLowerCase()}` : DISCOVERY_HANDOFF_CAMPAIGN;

/** The longest window the click report answers in one call. */
export const DISCOVERY_REPORT_MAX_DAYS = 366;
/** The window when `from` is not sent. */
export const DISCOVERY_REPORT_DEFAULT_DAYS = 30;

/**
 * The click report's window and narrowing. `from` and `to` are ISO instants;
 * `to` defaults to now and `from` to DISCOVERY_REPORT_DEFAULT_DAYS before it;
 * the window may not exceed DISCOVERY_REPORT_MAX_DAYS (the server checks the
 * resolved window). `brand` keeps handoffs whose page named that brand;
 * `companyId` keeps one company's.
 */
export const DiscoveryClickReportQuery = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  brand: DiscoveryBrand.optional(),
  companyId: z.string().uuid().optional(),
});
export type DiscoveryClickReportQuery = z.infer<typeof DiscoveryClickReportQuery>;

const Clicks = z.number().int().min(0);
/** Counts only. There is no visitor data in the record to report. */
export const DiscoveryClickReport = z.object({
  from: Iso,
  to: Iso,
  /** Every handoff in the window, after narrowing. */
  total: Clicks,
  /** Of those, how many went to a first-party page (and so carried the attribution ids). */
  firstParty: Clicks,
  /** By the brand surface the page named; null when it named none or the record did not carry it. Most first. */
  byBrand: z.array(z.object({ brand: DiscoveryBrand.nullable(), clicks: Clicks })),
  /** Most first. */
  byPurpose: z.array(z.object({ purpose: DiscoveryLinkPurpose, clicks: Clicks })),
  /** UTC days, oldest first; a day with no handoff is absent. */
  byDay: z.array(z.object({ day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), clicks: Clicks })),
  /** At most twenty, most first. */
  topCompanies: z.array(z.object({ id: z.string().uuid(), slug: DiscoverySlug, displayName: z.string(), clicks: Clicks })),
  /** At most twenty, most first. */
  topAuctions: z.array(z.object({ id: z.string().uuid(), slug: DiscoverySlug, title: z.string(), companySlug: DiscoverySlug, clicks: Clicks })),
});
export type DiscoveryClickReport = z.infer<typeof DiscoveryClickReport>;

// -- cut 2b-4: what the directory's own pages measured --------------------------

/**
 * What the directory's pages tell the API about a page load, and the operator's report of it. Four numbers
 * stay four numbers and are never added or renamed into one another:
 *
 *   views            a page was seen (a VIEW event)
 *   visitors         distinct visitor values: a keyed hash of (UTC day, address, user agent) made on the
 *                    server and thrown away with the request. The day is inside the hash, so this is
 *                    visitors PER DAY (a person on three days is three), nothing links two days, and
 *                    "returning visitor" is not measured
 *   engagedVisits    page loads that stayed in front of the visitor at least DISCOVERY_TRAFFIC_ENGAGED_MS
 *   clicks           handoffs through /out/:linkId (cut 3): attempted, never a verified arrival
 *
 * A verified arrival (the company's own site confirming the visit) is not measured at all yet; only a
 * first-party storefront can confirm one, through the hn_asset id a first-party handoff carries.
 *
 * The beacon path is not in DISCOVERY_ROUTES: its answer is a 204, not a shape (like /out/:linkId).
 * It exists only while DISCOVERY_TRAFFIC_ENABLED=1 with its key and origins, takes no cookie and sets
 * none, honors Do Not Track and Global Privacy Control, and records nothing about a robot.
 */
export const DISCOVERY_TRAFFIC_PATH = '/discovery/collect/event';
export const DISCOVERY_TRAFFIC_KINDS = ['VIEW', 'ENGAGED'] as const;
export const DISCOVERY_TRAFFIC_SURFACES = ['HOME', 'AUCTION_LIST', 'COMPANY_LIST', 'AUCTION', 'COMPANY', 'ALERTS', 'OTHER'] as const;
export const DISCOVERY_TRAFFIC_REFERRERS = ['DIRECT', 'SEARCH', 'SOCIAL', 'ALERT', 'SITE', 'OTHER'] as const;
export const DiscoveryTrafficKind = z.enum(DISCOVERY_TRAFFIC_KINDS);
export const DiscoveryTrafficSurface = z.enum(DISCOVERY_TRAFFIC_SURFACES);
export const DiscoveryTrafficReferrer = z.enum(DISCOVERY_TRAFFIC_REFERRERS);
export type DiscoveryTrafficKind = z.infer<typeof DiscoveryTrafficKind>;
export type DiscoveryTrafficSurface = z.infer<typeof DiscoveryTrafficSurface>;
export type DiscoveryTrafficReferrer = z.infer<typeof DiscoveryTrafficReferrer>;
/** A page load counts as an engaged visit once it was in front of the visitor this long (ms). */
export const DISCOVERY_TRAFFIC_ENGAGED_MS = 10_000;
/** The most time one page load may report (ms); more is a tab left open. */
export const DISCOVERY_TRAFFIC_MAX_ENGAGED_MS = 600_000;

/**
 * One report from a page load. `viewId` is made by the page for that load (a random uuid, never kept
 * anywhere else) so a VIEW and its ENGAGED are one visit and a retry is the same fact. A page names
 * the record it is about and nothing else: an AUCTION page its `auctionId` (the company is read from the
 * auction, never taken from the browser), a COMPANY page its `companyId`, every other surface neither.
 * `region`, `category`, `searched` and `results` describe a list page's narrowing, never the words typed.
 */
export const DiscoveryTrafficEventBody = z
  .object({
    brand: DiscoveryBrand,
    kind: DiscoveryTrafficKind,
    surface: DiscoveryTrafficSurface,
    viewId: z.string().uuid(),
    // The record a page is about, by the handle the page already holds: the company's slug, the auction's
    // short id. A page never holds an internal id, so it cannot be asked to send one.
    companySlug: DiscoverySlug.optional(),
    auctionShortId: DiscoveryShortId.optional(),
    referrer: DiscoveryTrafficReferrer.optional(),
    campaign: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/).optional(),
    category: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/).optional(),
    region: z.string().regex(/^[A-Z]{2}$/).optional(),
    searched: z.boolean().optional(),
    results: z.number().int().min(0).max(1_000_000).optional(),
    engagedMs: z.number().int().min(1).max(DISCOVERY_TRAFFIC_MAX_ENGAGED_MS).optional(),
  })
  .strict()
  .superRefine((b, ctx) => {
    const bad = (path: string, message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if ((b.kind === 'ENGAGED') !== (b.engagedMs !== undefined)) bad('engagedMs', 'engagedMs belongs to an ENGAGED report, and an ENGAGED report needs it');
    if (b.surface === 'AUCTION') { if (!b.auctionShortId) bad('auctionShortId', 'an AUCTION page names its auction'); if (b.companySlug) bad('companySlug', 'the company is read from the auction'); }
    else if (b.surface === 'COMPANY') { if (!b.companySlug) bad('companySlug', 'a COMPANY page names its company'); if (b.auctionShortId) bad('auctionShortId', 'a COMPANY page has no auction'); }
    else { if (b.companySlug) bad('companySlug', 'this page is about no company'); if (b.auctionShortId) bad('auctionShortId', 'this page is about no auction'); }
  });
export type DiscoveryTrafficEventBody = z.infer<typeof DiscoveryTrafficEventBody>;

/** Same window and narrowing as the click report: from, to, brand surface, one company. */
export const DiscoveryTrafficReportQuery = DiscoveryClickReportQuery;
export type DiscoveryTrafficReportQuery = DiscoveryClickReportQuery;

/** The four measures, kept apart. */
export const DiscoveryTrafficMeasures = z.object({ views: Clicks, visitors: Clicks, engagedVisits: Clicks, clicks: Clicks });
export type DiscoveryTrafficMeasures = z.infer<typeof DiscoveryTrafficMeasures>;
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Counts only. There is nothing about a person in the record to report. */
export const DiscoveryTrafficReport = z.object({
  from: Iso,
  to: Iso,
  /** Everything in the window, after narrowing. `searches` are list pages shown for a words query (a search impression). */
  totals: DiscoveryTrafficMeasures.extend({ searches: Clicks }),
  /** By the brand surface the page said it was. Most views first. */
  byBrand: z.array(DiscoveryTrafficMeasures.extend({ brand: DiscoveryBrand })),
  /** Where the visit came from, as a class. Most views first. Clicks are not attributed to a referrer. */
  byReferrer: z.array(z.object({ referrer: DiscoveryTrafficReferrer, views: Clicks, visitors: Clicks })),
  /** The page types. Most views first. */
  bySurface: z.array(z.object({ surface: DiscoveryTrafficSurface, views: Clicks, visitors: Clicks, engagedVisits: Clicks })),
  /** UTC days, oldest first; a day with nothing is absent. */
  byDay: z.array(DiscoveryTrafficMeasures.extend({ day: Day })),
  /** At most twenty, most views first. A company's views include its auctions' pages. */
  topCompanies: z.array(DiscoveryTrafficMeasures.extend({ id: z.string().uuid(), slug: DiscoverySlug, displayName: z.string() })),
  /** At most twenty, most views first. */
  topAuctions: z.array(DiscoveryTrafficMeasures.extend({ id: z.string().uuid(), slug: DiscoverySlug, title: z.string(), companySlug: DiscoverySlug })),
  /** The campaign ids pages arrived with, at most twenty. */
  byCampaign: z.array(z.object({ campaign: z.string(), views: Clicks, visitors: Clicks })),
  /** What visitors narrowed to (the interest in a category), at most twenty. */
  topCategories: z.array(z.object({ category: z.string(), views: Clicks, visitors: Clicks })),
  /** The state or province lists were narrowed to, at most twenty. */
  topRegions: z.array(z.object({ region: z.string(), views: Clicks, visitors: Clicks })),
  /**
   * Views of auction pages, grouped by the discovery source each auction came from (an auction that came
   * from two sources counts under both). The contribution of a source, not a partition. At most twenty.
   */
  bySource: z.array(z.object({ sourceKey: z.string(), displayName: z.string(), views: Clicks, visitors: Clicks })),
  /**
   * Views of auction pages, grouped by the agent that gave evidence for each auction (an auction two agents saw counts
   * under both). The contribution of an agent, not a partition. At most twenty.
   */
  byAgent: z.array(z.object({ agentKey: z.string(), displayName: z.string(), views: Clicks, visitors: Clicks })),
});
export type DiscoveryTrafficReport = z.infer<typeof DiscoveryTrafficReport>;

// -- cut 4a: opt-in alerts -----------------------------------------------------

/**
 * Opt-in auction alerts, one brand at a time (cut 4a). The HipNumber and
 * LotNumber audiences are separate lists: a subscription is (brand, email), an
 * unsubscribe in one never touches the other, and no response here ever echoes
 * an email address.
 *
 * The visitor routes are anonymous WRITES, so they have their own access class,
 * 'visitor': POST only, under /discovery/alerts/, never cached, rate-limited.
 * Signup is double opt-in: the visitor's request records a PENDING subscription
 * and emails a confirmation link; the person is ACTIVE only after following it.
 * The subscribe answer is the same for a new address, a pending one, an active
 * one and a suppressed one, so the form cannot be used to learn who is on the
 * list. Every token failure answers one 400, `invalid_token`.
 *
 * The tokens are HMACs minted by the server: the confirm token covers the
 * subscription id and a one-time nonce (it works once and expires), the manage
 * token covers the id alone (it never changes, so an unsubscribe link keeps
 * working from the oldest email). Neither is stored.
 */
export const DISCOVERY_ALERT_FREQUENCIES = ['DAILY', 'WEEKLY'] as const;
export const DiscoveryAlertFrequency = z.enum(DISCOVERY_ALERT_FREQUENCIES);
export type DiscoveryAlertFrequency = z.infer<typeof DiscoveryAlertFrequency>;

/** PENDING until the confirmation link is used; SUPPRESSED is ours (a bounce, a complaint, an operator's entry) and no signup undoes it. */
export const DISCOVERY_SUBSCRIPTION_STATES = ['PENDING', 'ACTIVE', 'UNSUBSCRIBED', 'SUPPRESSED'] as const;
export const DiscoverySubscriptionState = z.enum(DISCOVERY_SUBSCRIPTION_STATES);
export type DiscoverySubscriptionState = z.infer<typeof DiscoverySubscriptionState>;

export const DISCOVERY_SUBSCRIPTION_EVENT_KINDS = [
  'SIGNUP', 'CONFIRM_SENT', 'CONFIRM_FAILED', 'CONFIRMED', 'PREFERENCES_UPDATED', 'UNSUBSCRIBED', 'SUPPRESSED',
] as const;
export const DiscoverySubscriptionEventKind = z.enum(DISCOVERY_SUBSCRIPTION_EVENT_KINDS);
export type DiscoverySubscriptionEventKind = z.infer<typeof DiscoverySubscriptionEventKind>;

/** The shape of an address the directory will keep: ASCII, lowercase, one @. The database checks the same rule. */
export const DISCOVERY_ALERT_EMAIL_SHAPE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const AlertEmail = z.string().trim().toLowerCase().max(254).regex(DISCOVERY_ALERT_EMAIL_SHAPE, 'an email address like name@example.com');
const AlertPostalCode = z.string().regex(/^[0-9]{5}$/, 'a five-digit ZIP code');
const AlertRadius = z.number().int().min(5).max(500);
const AlertFirstName = z.string().trim().min(1).max(80);
const AlertCampaignId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/, 'a lowercase campaign id');
/** `<subscription id>.<43 characters of base64url HMAC>`. Opaque; carries no address. */
export const DISCOVERY_ALERT_TOKEN_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9_-]{43}$/;
export const DiscoveryAlertToken = z.string().regex(DISCOVERY_ALERT_TOKEN_SHAPE, 'a link token from an alert email');

/**
 * The signup form. `consent` is the visitor's explicit tick and must be the
 * literal `true`; `consentVersion` names the wording the form showed and must
 * be the one the server is currently running (otherwise the answer is 422
 * `consent_version_mismatch` and the form reloads its wording). Nothing here is
 * a bidding registration or a payment. SMS is not offered.
 */
export const DiscoveryAlertSubscribeBody = z.object({
  brand: DiscoveryBrand,
  email: AlertEmail,
  firstName: AlertFirstName.optional(),
  postalCode: AlertPostalCode.optional(),
  radiusMiles: AlertRadius.optional(),
  categories: z.array(Category).max(12).optional(),
  frequency: DiscoveryAlertFrequency.optional(),
  consent: z.literal(true),
  consentVersion: z.string().min(1).max(40),
  campaignId: AlertCampaignId.optional(),
}).strict().refine((b) => b.radiusMiles === undefined || b.postalCode !== undefined, { message: 'a radius needs a ZIP code', path: ['radiusMiles'] });
export type DiscoveryAlertSubscribeBody = z.infer<typeof DiscoveryAlertSubscribeBody>;

/** Always the same, whatever the address's state. */
export const DiscoveryAlertSubscribeResult = z.object({ status: z.literal('confirmation_requested') });
export type DiscoveryAlertSubscribeResult = z.infer<typeof DiscoveryAlertSubscribeResult>;

export const DiscoveryAlertTokenBody = z.object({ token: DiscoveryAlertToken }).strict();
export type DiscoveryAlertTokenBody = z.infer<typeof DiscoveryAlertTokenBody>;

/** Following the confirmation link. `manageToken` lets the page offer preferences and unsubscribe at once. */
export const DiscoveryAlertConfirmResult = z.object({
  brand: DiscoveryBrand,
  state: z.literal('ACTIVE'),
  manageToken: DiscoveryAlertToken,
});
export type DiscoveryAlertConfirmResult = z.infer<typeof DiscoveryAlertConfirmResult>;

/** Idempotent, and the same answer for a suppressed address: the visitor's wish (no mail) is already true. */
export const DiscoveryAlertUnsubscribeResult = z.object({ brand: DiscoveryBrand, state: z.literal('UNSUBSCRIBED') });
export type DiscoveryAlertUnsubscribeResult = z.infer<typeof DiscoveryAlertUnsubscribeResult>;

/** `null` clears a field; an omitted field is left alone; an empty body (just the token) changes nothing and answers the current preferences. */
export const DiscoveryAlertPreferencesBody = z.object({
  token: DiscoveryAlertToken,
  firstName: AlertFirstName.nullable().optional(),
  postalCode: AlertPostalCode.nullable().optional(),
  radiusMiles: AlertRadius.nullable().optional(),
  categories: z.array(Category).max(12).optional(),
  frequency: DiscoveryAlertFrequency.optional(),
}).strict();
export type DiscoveryAlertPreferencesBody = z.infer<typeof DiscoveryAlertPreferencesBody>;

export const DiscoveryAlertPreferences = z.object({
  brand: DiscoveryBrand,
  state: z.literal('ACTIVE'),
  firstName: z.string().nullable(),
  postalCode: z.string().nullable(),
  radiusMiles: z.number().int().nullable(),
  categories: z.array(z.string()),
  frequency: DiscoveryAlertFrequency,
});
export type DiscoveryAlertPreferences = z.infer<typeof DiscoveryAlertPreferences>;

/** The window and narrowing of the subscriber report: the same rules as the click report's. */
export const DiscoverySubscriberReportQuery = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  brand: DiscoveryBrand.optional(),
});
export type DiscoverySubscriberReportQuery = z.infer<typeof DiscoverySubscriberReportQuery>;

/**
 * Counts only: no address, no name, no ZIP, no identity of any kind. `byBrand`
 * lists the brands (both, unless one was asked for) with how many subscriptions
 * sit in each state NOW; `events` counts what happened inside the window, by
 * brand and kind, kinds with none omitted.
 */
export const DiscoverySubscriberReport = z.object({
  from: Iso,
  to: Iso,
  byBrand: z.array(z.object({
    brand: DiscoveryBrand,
    pending: z.number().int().min(0),
    active: z.number().int().min(0),
    unsubscribed: z.number().int().min(0),
    suppressed: z.number().int().min(0),
  })),
  events: z.array(z.object({ brand: DiscoveryBrand, kind: DiscoverySubscriptionEventKind, count: z.number().int().min(0) })),
});
export type DiscoverySubscriberReport = z.infer<typeof DiscoverySubscriberReport>;

/**
 * The operator's do-not-mail entry (a bounce, a complaint, a request received
 * another way). It creates the subscription row when the address was never on
 * the list, so a later signup is refused too. The reason is kept in the audit
 * row, never in the subscription.
 */
export const SuppressDiscoverySubscriberBody = z.object({
  brand: DiscoveryBrand,
  email: AlertEmail,
  reason: z.string().trim().min(1).max(200),
}).strict();
export type SuppressDiscoverySubscriberBody = z.infer<typeof SuppressDiscoverySubscriberBody>;

export const SuppressDiscoverySubscriberResult = z.object({
  brand: DiscoveryBrand,
  state: z.literal('SUPPRESSED'),
  /** True when the address was not on the list before. */
  created: z.boolean(),
});
export type SuppressDiscoverySubscriberResult = z.infer<typeof SuppressDiscoverySubscriberResult>;

// -- cut 4b-3: what a person can ask of us about their own address ------------------------------------------

/**
 * A person asks to see what the directory holds about their address (subject access), or asks for it to be erased.
 * Both are answered by POST, never by a URL, so the address stays out of every access log, and both take the same
 * three things as a do-not-mail entry: the brand's list, the address, and a reason that names the request. The
 * reason is kept in the audit row with the subscription id, never with the address.
 */
export const ExportDiscoverySubscriberBody = SuppressDiscoverySubscriberBody;
export type ExportDiscoverySubscriberBody = z.infer<typeof ExportDiscoverySubscriberBody>;
export const EraseDiscoverySubscriberBody = SuppressDiscoverySubscriberBody;
export type EraseDiscoverySubscriberBody = z.infer<typeof EraseDiscoverySubscriberBody>;

/** One line of the proof of consent: what happened, when, and which wording the person agreed to. No address, ever. */
export const DiscoverySubscriberExportEvent = z.object({
  at: Iso,
  kind: DiscoverySubscriptionEventKind,
  consentVersion: z.string().nullable(),
  consentSource: z.string().nullable(),
  campaignId: z.string().nullable(),
  detail: z.string().nullable(),
});
export type DiscoverySubscriberExportEvent = z.infer<typeof DiscoverySubscriberExportEvent>;

export const ExportDiscoverySubscriberResult = z.object({
  brand: DiscoveryBrand,
  /** False when the address was never on this brand's list, or was erased: nothing is held. */
  found: z.boolean(),
  subscription: z.object({
    id: z.string().uuid(),
    email: z.string(),
    firstName: z.string().nullable(),
    postalCode: z.string().nullable(),
    radiusMiles: z.number().int().nullable(),
    categories: z.array(z.string()),
    frequency: z.string(),
    state: z.string(),
    consentVersion: z.string(),
    consentSource: z.string(),
    campaignId: z.string().nullable(),
    confirmedAt: Iso.nullable(),
    unsubscribedAt: Iso.nullable(),
    createdAt: Iso,
    updatedAt: Iso,
  }).nullable(),
  events: z.array(DiscoverySubscriberExportEvent),
});
export type ExportDiscoverySubscriberResult = z.infer<typeof ExportDiscoverySubscriberResult>;

export const EraseDiscoverySubscriberResult = z.object({
  brand: DiscoveryBrand,
  /** False when the address was never on this brand's list, or was already erased: nothing changed. */
  found: z.boolean(),
  /** The name, ZIP, radius, categories and preferences are gone, and so is the address unless it is kept (below). */
  erased: z.boolean(),
  /**
   * True when the row was on the do-not-mail list (a bounce, a complaint, an operator's entry): everything about the
   * person is erased except the address, which stays on purpose so nobody mails it again.
   */
  keptAddress: z.boolean(),
});
export type EraseDiscoverySubscriberResult = z.infer<typeof EraseDiscoverySubscriberResult>;

// -- cut 6c: the takedown and the image desk ----------------------------------

/**
 * The INC or CHG number (ADR-58) of the record that asked for a takedown. Required: a takedown is never made on
 * its own say-so, it answers an incident or a change a person opened, and that record is the paper trail. The
 * server also checks the record exists; control never writes governance (ADR-61), so it cannot open one.
 */
export const DiscoveryIncidentRef = z.string().trim().regex(/^(INC|CHG)[0-9]{6,}$/, 'an INC or CHG number such as INC000123');

/** What a takedown reaches. Every scope is cross-brand: a directory image has no brand. */
export const DiscoveryTakedownScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('IMAGE'), imageId: z.string().uuid() }).strict(),
  /** Every image of one auction. */
  z.object({ kind: z.literal('AUCTION'), auctionId: z.string().uuid() }).strict(),
  /** Every image of one company, its logo and the pictures of all its auctions. */
  z.object({ kind: z.literal('COMPANY'), companyId: z.string().uuid() }).strict(),
  /** Every image one source ever listed, and the source itself becomes TAKEDOWN_BLOCKED. */
  z.object({ kind: z.literal('SOURCE'), sourceKey: DiscoverySourceKey }).strict(),
]);
export type DiscoveryTakedownScope = z.infer<typeof DiscoveryTakedownScope>;

/**
 * A SuperAdmin takedown. The images in scope become REMOVED (never revived), their original addresses are blocked
 * for good so no source can bring them back, and, unless `blockContent` is false, so are their bytes (a content
 * hash), which also removes every other row holding the same bytes. The stored objects are deleted and the CDN's
 * copy purged by the worker. `dryRun` counts what would happen and writes nothing.
 */
export const DiscoveryTakedownBody = z.object({
  scope: DiscoveryTakedownScope,
  reason: z.string().trim().min(1).max(1000),
  incidentRef: DiscoveryIncidentRef,
  /** Default true. */
  blockContent: z.boolean().optional(),
  dryRun: z.boolean().optional(),
}).strict();
export type DiscoveryTakedownBody = z.infer<typeof DiscoveryTakedownBody>;

export const DiscoveryTakedownResult = z.object({
  dryRun: z.boolean(),
  scope: z.enum(['IMAGE', 'AUCTION', 'COMPANY', 'SOURCE']),
  incidentRef: z.string(),
  /** Rows now REMOVED: the ones in scope plus any other row that holds the same bytes. */
  imagesRemoved: z.number().int().nonnegative(),
  /** Of those, rows outside the scope removed because they held blocked bytes. */
  twinsRemoved: z.number().int().nonnegative(),
  /** Of those, rows whose bytes were in our storage. */
  storedRemoved: z.number().int().nonnegative(),
  urlBlocksAdded: z.number().int().nonnegative(),
  hashBlocksAdded: z.number().int().nonnegative(),
  /** Hashes queued for the worker to delete from storage and purge from the CDN. */
  cleanupQueued: z.number().int().nonnegative(),
  /** True when the scope was a source and its class is now TAKEDOWN_BLOCKED. */
  sourceBlocked: z.boolean(),
});
export type DiscoveryTakedownResult = z.infer<typeof DiscoveryTakedownResult>;

export const DiscoveryImageListQuery = z.object({
  status: DiscoveryImageStatus.optional(),
  sourceKey: DiscoverySourceKey.optional(),
  companyId: z.string().uuid().optional(),
  auctionId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type DiscoveryImageListQuery = z.infer<typeof DiscoveryImageListQuery>;

/** One image row for the operator: provenance, storage state, and what a page would show of it right now. */
export const DiscoveryImage = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  auctionId: z.string().uuid().nullable(),
  sourceKey: z.string(),
  role: DiscoveryImageRole,
  sourceUrl: z.string(),
  sourcePageUrl: z.string().nullable(),
  usageClass: DiscoverySourceClass,
  status: DiscoveryImageStatus,
  statusReason: z.string(),
  /** What a public page would show of it now: derived from the source's current class and permission and the block list. */
  show: z.enum(['FULL', 'PREVIEW', 'NONE']),
  contentSha256: z.string().nullable(),
  contentType: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  bytes: z.number().int().nullable(),
  /** The derived sizes that exist (thumb, card, gallery, full, card_jpg). */
  variants: z.array(z.string()),
  attempts: z.number().int().nonnegative(),
  nextAttemptAt: Iso.nullable(),
  fetchedAt: Iso.nullable(),
  lastCheckedAt: Iso.nullable(),
  firstSeenAt: Iso,
  lastSeenAt: Iso,
});
export type DiscoveryImage = z.infer<typeof DiscoveryImage>;
export const DiscoveryImageList = page(DiscoveryImage);
export type DiscoveryImageList = z.infer<typeof DiscoveryImageList>;

const Count = z.number().int().nonnegative();
/** The image desk at a glance: where every image is, how many blocks stand, and whether the worker has caught up with takedowns. */
export const DiscoveryImageSummary = z.object({
  byStatus: z.object({ PENDING: Count, STORED: Count, FAILED: Count, SOURCE_GONE: Count, REMOVED: Count }),
  blocks: z.object({ sha256: Count, url: Count }),
  cleanup: z.object({ pending: Count, done: Count, failed: Count }),
});
export type DiscoveryImageSummary = z.infer<typeof DiscoveryImageSummary>;

// -- the route table ---------------------------------------------------------

const NoBody = z.undefined();

// -- cut 7: agents ------------------------------------------------------------

/**
 * Continuous discovery (ADR-71 cut 7). An agent is a program that finds upcoming auctions and submits what it
 * saw, with the page it saw it on. It holds ONE credential, `hna_<prefix>_<secret>`, that can do one thing: submit
 * observations, through POST /discovery/ingest, to the one PERMITTED source it was issued for. It cannot read the
 * directory, publish on its own authority, or reach anything else. Only a hash of the secret is stored; the secret
 * is shown once, when the agent is created, and a lost one means a new agent.
 *
 * The backend does the reconciling, not the agent: every observation goes through the same importer every other
 * source uses (rights gate, safe addresses, no auction that has already closed, no start more than 800 days away),
 * a company named by its website is matched to the company already in the directory instead of being copied, an
 * auction that is already here from another source is kept as a second witness and never overwrites it, and what is
 * left is published only when the agent was issued with autoPublish. This path is not in DISCOVERY_ROUTES (like the
 * click handoff and the traffic beacon it answers a machine, with its own credential, not an operator).
 */
export const DISCOVERY_INGEST_PATH = '/discovery/ingest';
/** The same door for the lots of auctions the agent's own source already holds (cut 8d). */
export const DISCOVERY_INGEST_LOTS_PATH = '/discovery/ingest/lots';
/** The most observations one submission may carry, whatever an agent's own limit says. */
export const DISCOVERY_AGENT_MAX_RECORDS = 500;
/** What a credential looks like; the door refuses anything else before it reads the database. */
export const DISCOVERY_AGENT_TOKEN_PATTERN = /^hna_[0-9a-f]{8}_[A-Za-z0-9_-]{32}$/;

export const DiscoveryAgentKey = z.string().regex(/^[a-z][a-z0-9-]{1,47}$/, 'lowercase letters, digits and single hyphens, 2 to 48 characters');
export const DiscoveryAgentStatus = z.enum(['ACTIVE', 'REVOKED']);
export type DiscoveryAgentStatus = z.infer<typeof DiscoveryAgentStatus>;

export const DiscoveryAgent = z.object({
  id: z.string().uuid(),
  agentKey: z.string(),
  displayName: z.string(),
  sourceKey: z.string(),
  status: DiscoveryAgentStatus,
  /** The first part of the credential, so a person can tell which one an agent holds. Not a secret. */
  tokenPrefix: z.string(),
  autoPublish: z.boolean(),
  perMinute: z.number().int(),
  maxRecords: z.number().int(),
  createdBy: z.string(),
  createdAt: Iso,
  lastUsedAt: Iso.nullable(),
  revokedAt: Iso.nullable(),
  revokedBy: z.string().nullable(),
  revokedReason: z.string().nullable(),
});
export type DiscoveryAgent = z.infer<typeof DiscoveryAgent>;

/** The answer to creating an agent: the only time the credential is shown. */
export const DiscoveryAgentCreated = DiscoveryAgent.extend({ token: z.string() });
export type DiscoveryAgentCreated = z.infer<typeof DiscoveryAgentCreated>;

const AgentLimits = {
  /** Submissions a minute; the door answers 429 beyond it. */
  perMinute: z.number().int().min(1).max(600).optional(),
  /** Observations in one submission. */
  maxRecords: z.number().int().min(1).max(DISCOVERY_AGENT_MAX_RECORDS).optional(),
};

export const CreateDiscoveryAgentBody = z.object({
  agentKey: DiscoveryAgentKey,
  displayName: z.string().trim().min(1).max(200),
  /** An existing source that is PERMITTED. The agent writes under it and nothing else. */
  sourceKey: DiscoverySourceKey,
  /** Default false: what the agent sends waits in PENDING_REVIEW for a person. */
  autoPublish: z.boolean().optional(),
  ...AgentLimits,
}).strict();
export type CreateDiscoveryAgentBody = z.infer<typeof CreateDiscoveryAgentBody>;

export const UpdateDiscoveryAgentBody = z.object({
  displayName: z.string().trim().min(1).max(200).optional(),
  autoPublish: z.boolean().optional(),
  ...AgentLimits,
}).strict().refine((b) => Object.keys(b).length > 0, { message: 'change at least one thing' });
export type UpdateDiscoveryAgentBody = z.infer<typeof UpdateDiscoveryAgentBody>;

export const RevokeDiscoveryAgentBody = z.object({ reason: z.string().trim().min(1).max(200) }).strict();
export type RevokeDiscoveryAgentBody = z.infer<typeof RevokeDiscoveryAgentBody>;

/** Where and when an agent saw an auction, and a few words of what the page said. Kept as the record's evidence. */
export const DiscoveryEvidence = z.object({
  pageUrl: DiscoveryTargetUrl,
  /** Defaults to now. Not in the future. */
  observedAt: z.string().datetime({ offset: true }).optional(),
  excerpt: z.string().trim().max(500).optional(),
}).strict();
export type DiscoveryEvidence = z.infer<typeof DiscoveryEvidence>;

/**
 * One thing an agent saw: an auction, its company (an existing one by `companySlug`, or in full, matched by its
 * website to the company already in the directory when there is one), and the evidence.
 */
export const DiscoveryObservation = z.object({
  companySlug: DiscoverySlug.optional(),
  company: DiscoveryCompanyInput.optional(),
  auction: DiscoveryAuctionInput,
  evidence: DiscoveryEvidence,
}).strict().refine((o) => (o.companySlug === undefined) !== (o.company === undefined), { message: 'give exactly one of company or companySlug', path: ['company'] });
export type DiscoveryObservation = z.infer<typeof DiscoveryObservation>;

/**
 * A submission. Each observation is checked on its own: one that is not shaped right is refused by its auction's
 * externalId (or its position) and the rest go on. `dryRun` does everything and writes nothing.
 */
export const DiscoverySubmissionBody = z.object({
  observations: z.array(z.unknown()).min(1).max(DISCOVERY_AGENT_MAX_RECORDS),
  dryRun: z.boolean().optional(),
  /** What the run cost the agent, in millionths of a dollar. Self-reported, for the cost report. */
  costMicros: z.number().int().min(0).max(1_000_000_000_000).optional(),
  note: z.string().trim().max(500).optional(),
}).strict();
export type DiscoverySubmissionBody = z.infer<typeof DiscoverySubmissionBody>;

export const DiscoverySubmissionResult = z.object({
  /** Null for a dry run: nothing was recorded. */
  runId: z.string().uuid().nullable(),
  dryRun: z.boolean(),
  received: Clicks,
  /** Observations the directory took in: written (imported) or kept as a second witness (duplicates). */
  accepted: Clicks,
  imported: Clicks,
  created: Clicks,
  updated: Clicks,
  companiesCreated: Clicks,
  /** Companies named in full that were matched to one already in the directory by its website. */
  companiesMatched: Clicks,
  /** Auctions already in the directory from another source: evidence was added, nothing was overwritten. */
  duplicates: Clicks,
  published: Clicks,
  /** An operator had moved the record on; it stays where they put it. */
  leftAlone: Clicks,
  /** Already closed: never written. */
  skippedPast: Clicks,
  rejected: z.array(z.object({ ref: z.string(), reason: z.string() })),
});
export type DiscoverySubmissionResult = z.infer<typeof DiscoverySubmissionResult>;

/** The per-agent report's window and narrowing. Same window rules as the click report. */
export const DiscoveryAgentReportQuery = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  agentKey: DiscoveryAgentKey.optional(),
});
export type DiscoveryAgentReportQuery = z.infer<typeof DiscoveryAgentReportQuery>;

const AgentFigures = z.object({
  runs: Clicks,
  records: Clicks,
  imported: Clicks,
  created: Clicks,
  updated: Clicks,
  duplicates: Clicks,
  published: Clicks,
  skippedPast: Clicks,
  rejected: Clicks,
  companiesCreated: Clicks,
  companiesMatched: Clicks,
  /** (imported + duplicates) / records; null when no records. */
  acceptanceRate: z.number().min(0).max(1).nullable(),
  /** What the agents said their runs cost, in millionths of a dollar. */
  costMicros: Clicks,
  /** costMicros / (imported + duplicates), rounded; null when nothing was accepted or no cost was reported. */
  costPerAcceptedMicros: z.number().nullable(),
  /** Mean time the backend took per submission, in milliseconds; null when none finished. */
  avgDurationMs: z.number().nullable(),
});
export const DiscoveryAgentReport = z.object({
  from: Iso,
  to: Iso,
  totals: AgentFigures,
  agents: z.array(AgentFigures.extend({
    agentKey: z.string(),
    displayName: z.string(),
    sourceKey: z.string(),
    status: DiscoveryAgentStatus,
    lastRunAt: Iso.nullable(),
    /** The commonest reasons its observations were refused, at most five. */
    topRejections: z.array(z.object({ reason: z.string(), count: Clicks })),
  })),
});
export type DiscoveryAgentReport = z.infer<typeof DiscoveryAgentReport>;

// -- lots (cut 8a) ------------------------------------------------------------

/**
 * Cut 8a (ADR-71, docs/plan/discovery-directory-adr-71.md section 25): one item for sale inside a directory auction.
 * A directory lot is ABOUT a lot a source lists; it is never a lot of the platform: no tenant, no sale, no bid, and
 * nothing here connects it to the transactional bidding engine. One shape for every category; what only some
 * categories have (hours, horsepower, breed, sex) goes in a small flat `attributes` object.
 */
export const DISCOVERY_LOT_STATUSES = ['PENDING_REVIEW', 'PUBLISHED', 'HIDDEN', 'RETRACTED'] as const;
export const DiscoveryLotStatus = z.enum(DISCOVERY_LOT_STATUSES);
export type DiscoveryLotStatus = z.infer<typeof DiscoveryLotStatus>;

/** The same moves a company has: HIDDEN keeps a lot off the pages without retracting it; RETRACTED is final. */
export const DISCOVERY_LOT_TRANSITIONS: Readonly<Record<DiscoveryLotStatus, readonly DiscoveryLotStatus[]>> = {
  PENDING_REVIEW: ['PUBLISHED', 'HIDDEN', 'RETRACTED'],
  PUBLISHED: ['HIDDEN', 'RETRACTED'],
  HIDDEN: ['PUBLISHED', 'RETRACTED'],
  RETRACTED: [],
};
export const canMoveDiscoveryLot = (from: DiscoveryLotStatus, to: DiscoveryLotStatus): boolean => DISCOVERY_LOT_TRANSITIONS[from].includes(to);

/** What a price means. A directory knows a final price only when a source said so (SOLD_FOR). */
export const DISCOVERY_LOT_PRICE_KINDS = ['STARTING_BID', 'CURRENT_BID', 'ESTIMATE', 'BUY_NOW', 'SOLD_FOR'] as const;
export const DiscoveryLotPriceKind = z.enum(DISCOVERY_LOT_PRICE_KINDS);
export type DiscoveryLotPriceKind = z.infer<typeof DiscoveryLotPriceKind>;

/** At most this many extras on one lot. */
export const DISCOVERY_LOT_MAX_ATTRIBUTES = 20;
/** Keys are lowercase words ("hours", "four-wheel-drive"); values are short text, a finite number or a flag. */
export const DiscoveryLotAttributes = z.record(
  z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'a lowercase word like hours, horsepower or four-wheel-drive'),
  z.union([z.string().trim().max(200), z.number().finite(), z.boolean()]),
).refine((o) => Object.keys(o).length <= DISCOVERY_LOT_MAX_ATTRIBUTES, { message: `at most ${DISCOVERY_LOT_MAX_ATTRIBUTES} attributes` });
export type DiscoveryLotAttributes = z.infer<typeof DiscoveryLotAttributes>;

/** The largest price a lot may carry, in cents: a typo guard, not a business rule (one billion dollars). */
export const DISCOVERY_LOT_MAX_PRICE_CENTS = 100_000_000_000;

/** The lot half of a bulk import. externalId is the source's own id for the lot. */
export const DiscoveryLotInput = z.object({
  externalId: z.string().trim().min(1).max(200),
  title: Name,
  /** The number the auctioneer gave it ("12", "A-4"). */
  lotNumber: z.string().trim().min(1).max(40).optional(),
  description: Text.optional(),
  category: Category.optional(),
  manufacturer: Short.optional(),
  model: Short.optional(),
  year: z.number().int().min(1800).max(2200).optional(),
  condition: z.string().trim().max(80).optional(),
  city: Short.optional(),
  region: Short.optional(),
  /** Defaults to US. */
  country: Country.optional(),
  attributes: DiscoveryLotAttributes.optional(),
  /** A price is the number AND what it means; one without the other is refused. */
  priceCents: z.number().int().min(0).max(DISCOVERY_LOT_MAX_PRICE_CENTS).optional(),
  priceKind: DiscoveryLotPriceKind.optional(),
  /** ISO 4217, upper case. Defaults to USD. */
  currency: z.string().regex(/^[A-Z]{3}$/, 'three upper-case letters (ISO 4217)').optional(),
  /** When the source last saw the price. */
  priceAsOf: z.string().datetime({ offset: true }).optional(),
  /** When bidding on this lot closes, when the source says. */
  closesAt: z.string().datetime({ offset: true }).optional(),
  /** The one place a visitor is sent to bid on this lot. Required. */
  biddingUrl: DiscoveryTargetUrl,
  /** The lot's pictures (cut 8c). Absent: leave them as they are. Present (even empty): the source's whole list for the lot. */
  images: z.array(DiscoveryImageInput).max(DISCOVERY_MAX_IMAGES_PER_RECORD).optional(),
}).refine((l) => (l.priceCents === undefined) === (l.priceKind === undefined), { message: 'give priceCents and priceKind together', path: ['priceKind'] });
export type DiscoveryLotInput = z.infer<typeof DiscoveryLotInput>;

/**
 * One lot an agent saw (cut 8d): the id its source gave the AUCTION (what the agent wrote when it submitted the
 * auction, so an agent can only add lots to auctions its own source holds) and the lot itself. The same
 * importer as the bulk file runs it: the rights gate, safe addresses, no lot that has already closed, a lot
 * published only when its auction and company already are.
 */
export const DiscoveryAgentLotRecord = z.object({
  auctionExternalId: z.string().trim().min(1).max(200),
  lot: DiscoveryLotInput,
}).strict();
export type DiscoveryAgentLotRecord = z.infer<typeof DiscoveryAgentLotRecord>;

/** A lot submission: each record is checked on its own (a bad one is refused by its lot's externalId, the rest go on). */
export const DiscoveryLotSubmissionBody = z.object({
  records: z.array(z.unknown()).min(1).max(DISCOVERY_AGENT_MAX_RECORDS),
  dryRun: z.boolean().optional(),
}).strict();
export type DiscoveryLotSubmissionBody = z.infer<typeof DiscoveryLotSubmissionBody>;

export const DiscoveryLotSubmissionResult = z.object({
  dryRun: z.boolean(),
  received: Clicks,
  /** Lots written (created + updated). */
  accepted: Clicks,
  created: Clicks,
  updated: Clicks,
  /** Moved to PUBLISHED by this submission (only when the agent was issued with autoPublish). */
  published: Clicks,
  /** Publishing was asked for but the lot's auction or company is not published yet. */
  waiting: Clicks,
  /** An operator had moved the lot on; it stays where they put it. */
  leftAlone: Clicks,
  /** Already closed: never written. */
  skippedPast: Clicks,
  rejected: z.array(z.object({ ref: z.string(), reason: z.string() })),
});
export type DiscoveryLotSubmissionResult = z.infer<typeof DiscoveryLotSubmissionResult>;

export const DiscoveryLot = z.object({
  id: z.string().uuid(),
  shortId: DiscoveryShortId,
  slug: z.string(),
  auctionId: z.string().uuid(),
  auctionSlug: z.string(),
  auctionTitle: z.string(),
  companyId: z.string().uuid(),
  companySlug: z.string(),
  companyName: z.string(),
  lotNumber: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  category: z.string().nullable(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().int().nullable(),
  condition: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  country: Country,
  attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  priceCents: z.number().int().nullable(),
  priceKind: DiscoveryLotPriceKind.nullable(),
  currency: z.string(),
  priceAsOf: Iso.nullable(),
  closesAt: Iso.nullable(),
  status: DiscoveryLotStatus,
  statusReason: z.string(),
  statusChangedAt: Iso,
  publishedAt: Iso.nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: Iso,
  updatedAt: Iso,
  sources: z.array(DiscoveryProvenance),
  links: z.array(DiscoveryOutboundLink),
});
export type DiscoveryLot = z.infer<typeof DiscoveryLot>;

export const DiscoveryLotList = page(DiscoveryLot);
export type DiscoveryLotList = z.infer<typeof DiscoveryLotList>;

export const DiscoveryLotListQuery = z.object({
  status: DiscoveryLotStatus.optional(),
  auctionId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type DiscoveryLotListQuery = z.infer<typeof DiscoveryLotListQuery>;

export const SetDiscoveryLotStatusBody = z.object({
  status: DiscoveryLotStatus,
  reason: z.string().trim().max(1000).optional(),
});
export type SetDiscoveryLotStatusBody = z.infer<typeof SetDiscoveryLotStatusBody>;

// -- cut 8b: lots on the public pages ------------------------------------------

/**
 * A lot page is worth indexing when it has a way onward (its bidding link) and says what the thing is in at
 * least two ways: a category, a maker, a model, a year, or words of its own. A title alone is a row in a list.
 * Spelled again in SQL for the sitemap feed; one suite holds the two to the same answer.
 */
export const DISCOVERY_LOT_MIN_DETAILS = 2;
export const isIndexableDiscoveryLot = (l: {
  category: string | null; manufacturer: string | null; model: string | null; year: number | null; description: string; links: readonly unknown[];
}): boolean =>
  l.links.length > 0
  && [l.category !== null, l.manufacturer !== null, l.model !== null, l.year !== null, hasOwnWords(l.description)].filter(Boolean).length >= DISCOVERY_LOT_MIN_DETAILS;

/** What a price is, as the public sees it: an amount, what it means, and how old it is. Never a bare figure. */
export const PublicDiscoveryLotPrice = z.object({
  amountCents: z.number().int().min(0),
  kind: DiscoveryLotPriceKind,
  currency: z.string(),
  /** When the source last saw this price. */
  asOf: Iso.nullable(),
});
export type PublicDiscoveryLotPrice = z.infer<typeof PublicDiscoveryLotPrice>;

/** The auction a lot is in: enough for a line and a link, nothing that needs a second read. */
export const PublicDiscoveryLotAuction = z.object({
  slug: z.string(),
  shortId: DiscoveryShortId,
  title: z.string(),
  startsAt: Iso,
  endsAt: Iso.nullable(),
  timeZone: z.string(),
  ...PublicPlace,
});
export type PublicDiscoveryLotAuction = z.infer<typeof PublicDiscoveryLotAuction>;

/**
 * A lot as an anonymous visitor sees it: addressed by its short id (the slug is wording), with no id, status,
 * provenance, raw record or outbound address; the bidding link is an opaque id plus its host. The place is the
 * lot's own when it has one, else its auction's.
 */
export const PublicDiscoveryLot = z.object({
  slug: z.string(),
  shortId: DiscoveryShortId,
  title: z.string(),
  description: z.string(),
  lotNumber: z.string().nullable(),
  category: z.string().nullable(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().int().nullable(),
  condition: z.string().nullable(),
  ...PublicPlace,
  attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  price: PublicDiscoveryLotPrice.nullable(),
  closesAt: Iso.nullable(),
  publishedAt: Iso,
  /** When the record last changed; a sitemap's lastmod. */
  updatedAt: Iso,
  auction: PublicDiscoveryLotAuction,
  company: PublicDiscoveryCompanyCard,
  /** ACTIVE links only: the one place to bid. */
  links: z.array(PublicDiscoveryLink),
  /** The lot's pictures that may be shown, from the brand's own media host only. A list carries the cover alone. */
  images: z.array(PublicDiscoveryImage).default([]),
});
export type PublicDiscoveryLot = z.infer<typeof PublicDiscoveryLot>;

export const PublicDiscoveryLotList = page(PublicDiscoveryLot);
export type PublicDiscoveryLotList = z.infer<typeof PublicDiscoveryLotList>;

/**
 * The public lot list. `brand` is required, like the auction list. `q` is matched word by word against title,
 * maker, model, category, city and region; `auction` is an auction's short id (its lots, in lot-number order);
 * `when` is upcoming by default (a lot that has not closed, whose auction has not ended). Soonest closing first.
 */
export const PublicDiscoveryLotListQuery = z.object({
  brand: DiscoveryBrand,
  q: z.string().trim().max(200).optional(),
  category: Category.optional(),
  manufacturer: z.string().trim().min(1).max(200).optional(),
  region: z.string().trim().min(1).max(120).optional(),
  country: Country.optional(),
  auction: DiscoveryShortId.optional(),
  when: DiscoveryPublicWhen.optional(),
  limit: z.coerce.number().int().min(1).max(DISCOVERY_PUBLIC_MAX_LIMIT).optional(),
  offset: z.coerce.number().int().min(0).max(5000).optional(),
});
export type PublicDiscoveryLotListQuery = z.infer<typeof PublicDiscoveryLotListQuery>;

export interface DiscoveryRouteDef<Req extends z.ZodTypeAny, Res extends z.ZodTypeAny> {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Absolute path - NOT prefixed by /t/:tenantSlug. `:name` segments are params. */
  readonly path: string;
  readonly request: Req;
  readonly response: Res;
  /**
   * 'operator': the platform operator only, `no-store`, with provenance.
   * 'public': anonymous, rate-limited and cacheable; only PUBLISHED records,
   * never provenance, Public* shapes only. Every public route lives under
   * /discovery/public/ and is a GET; nothing else lives there.
   * 'visitor' (cut 4a): an anonymous WRITE - the alert signup, confirmation,
   * unsubscribe and preferences. POST only, under /discovery/alerts/, never
   * cached, rate-limited, and no answer ever echoes an email address.
   */
  readonly access: 'operator' | 'public' | 'visitor';
  readonly summary: string;
}
const dr = <Req extends z.ZodTypeAny, Res extends z.ZodTypeAny>(d: DiscoveryRouteDef<Req, Res>) => d;

export const DISCOVERY_ROUTES = {
  listDiscoverySources: dr({
    method: 'GET', path: '/discovery/sources', request: NoBody, response: z.array(DiscoverySource), access: 'operator',
    summary: 'Every discovery source with its permission status and how many records trace to it. Operator only.',
  }),
  upsertDiscoverySource: dr({
    method: 'PUT', path: '/discovery/sources/:sourceKey', request: UpsertDiscoverySourceBody, response: DiscoverySource, access: 'operator',
    summary: 'Create or replace a discovery source: its name, how records arrive, and whether HipNumber may use it (permissionStatus with its basis in words). Nothing is imported from a source that is not PERMITTED. Operator only, audited as discovery.source.',
  }),
  importDiscoveryRecord: dr({
    method: 'POST', path: '/discovery/sources/:sourceKey/import', request: DiscoveryManualImportBody, response: DiscoveryImportResult, access: 'operator',
    summary: 'Manually import one company, and optionally one of its auctions, from a PERMITTED source; a company alone is a complete record, an auction never arrives without its company. Idempotent on (source, externalId): a repeat updates, never duplicates. New records start PENDING_REVIEW; a repeat never changes status. Outbound targets are validated and stored as opaque link ids. dryRun writes nothing. Never creates a tenant or a sale. Operator only, audited as discovery.import.',
  }),
  listDiscoveryCompanies: dr({
    method: 'GET', path: '/discovery/companies', request: NoBody, response: DiscoveryCompanyList, access: 'operator',
    summary: 'The review desk list of directory companies, with provenance. Filters: status, brand, search. Paged: check `remaining`. Operator only.',
  }),
  getDiscoveryCompany: dr({
    method: 'GET', path: '/discovery/companies/:slug', request: NoBody, response: DiscoveryCompany, access: 'operator',
    summary: 'One directory company in full, by slug or id, with its sources and links. Operator only.',
  }),
  setDiscoveryCompanyStatus: dr({
    method: 'POST', path: '/discovery/companies/:id/status', request: SetDiscoveryCompanyStatusBody, response: DiscoveryCompany, access: 'operator',
    summary: 'Move a company between PENDING_REVIEW, PUBLISHED, HIDDEN and RETRACTED along DISCOVERY_COMPANY_TRANSITIONS. RETRACTED is terminal. Operator only, audited as discovery.company.status.',
  }),
  bulkSetDiscoveryCompanyStatus: dr({
    method: 'POST', path: '/discovery/companies/status-bulk', request: BulkSetDiscoveryCompanyStatusBody, response: DiscoveryBulkStatusResult, access: 'operator',
    summary: 'Move many companies of ONE source in one call, along the same transitions as the single move: every company with a record from sourceKey that is `from` and matches the optional brand and search. dryRun counts and writes nothing; publishing needs the source PERMITTED; at most `limit` (ceiling 5000) move per call, and `remaining` says whether another round is needed. Operator only, one audit row discovery.company.status.bulk.',
  }),
  listDiscoveryAuctions: dr({
    method: 'GET', path: '/discovery/auctions', request: NoBody, response: DiscoveryAuctionList, access: 'operator',
    summary: 'The review desk list of directory auctions, with provenance. Filters: status, brand, companyId, search. Soonest first. Paged: check `remaining`. Operator only.',
  }),
  getDiscoveryAuction: dr({
    method: 'GET', path: '/discovery/auctions/:slug', request: NoBody, response: DiscoveryAuction, access: 'operator',
    summary: 'One directory auction in full, by slug or id, with its sources and links. Operator only.',
  }),
  setDiscoveryAuctionStatus: dr({
    method: 'POST', path: '/discovery/auctions/:id/status', request: SetDiscoveryAuctionStatusBody, response: DiscoveryAuction, access: 'operator',
    summary: 'Move an auction along DISCOVERY_AUCTION_TRANSITIONS (PENDING_REVIEW -> VERIFIED -> PUBLISHED -> ENDED -> ARCHIVED, or out to QUARANTINED, RETRACTED, SOURCE_BLOCKED, STALE). PUBLISHED also needs the company PUBLISHED and the source still PERMITTED. Operator only, audited as discovery.auction.status.',
  }),

  // Cut 2a: the public reads. Anonymous, cached briefly, rate-limited per
  // client address, and present only while DISCOVERY_PUBLIC_ENABLED is on.
  listPublicDiscoveryAuctions: dr({
    method: 'GET', path: '/discovery/public/auctions', request: NoBody, response: PublicDiscoveryAuctionList, access: 'public',
    summary: 'The public directory of auctions for one brand (brand is required). Only PUBLISHED auctions of PUBLISHED companies whose every source is still PERMITTED. Filters: q, region, country, category, when (upcoming by default). Soonest first. Paged, at most 50. No provenance, no ids, no addresses: a link is an opaque id plus its host. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  getPublicDiscoveryAuction: dr({
    method: 'GET', path: '/discovery/public/auctions/:slug', request: NoBody, response: PublicDiscoveryAuction, access: 'public',
    summary: 'One public auction by slug (never by id). 404 for anything that is not public, whether unknown, unpublished, hidden or from a source no longer PERMITTED. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  getPublicDiscoveryCompany: dr({
    method: 'GET', path: '/discovery/public/companies/:slug', request: NoBody, response: PublicDiscoveryCompany, access: 'public',
    summary: 'One public company by slug (never by id), with its website as an opaque link and its public auctions that have not ended. 404 for anything that is not public. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),

  // Cut 2b: what the pages of lotnumber.com (and later hipnum.com) read beyond
  // the three above. Same posture: anonymous, cached briefly, rate-limited per
  // client address, absent unless DISCOVERY_PUBLIC_ENABLED.
  getPublicDiscoveryAuctionByRef: dr({
    method: 'GET', path: '/discovery/public/auctions/ref/:shortId', request: NoBody, response: PublicDiscoveryAuction, access: 'public',
    summary: 'One public auction by its short id: the address in /auctions/{slug}-{short-id}/, where the slug is wording that may have changed since (the page then redirects to the current slug). 404 for anything that is not public, and for a malformed or unknown handle. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  listPublicDiscoveryCompanies: dr({
    method: 'GET', path: '/discovery/public/companies', request: NoBody, response: PublicDiscoveryCompanyList, access: 'public',
    summary: 'The public directory of auction companies for one brand (brand is required). Only PUBLISHED companies whose every source is still PERMITTED. Filters: q, region, country, category. Alphabetical. Paged, at most 50. Each row carries its count of upcoming public auctions. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  listPublicDiscoveryLots: dr({
    method: 'GET', path: '/discovery/public/lots', request: NoBody, response: PublicDiscoveryLotList, access: 'public',
    summary: 'The public directory of lots for one brand (brand is required). Only PUBLISHED lots of PUBLISHED auctions of PUBLISHED companies whose every source (the lot\'s too) is still PERMITTED. Filters: q, category, manufacturer, region, country, auction (a short id), when (upcoming by default). Soonest closing first, or in lot-number order within one auction. Paged, at most 50. A price carries its meaning and its age. No ids, no provenance, no addresses: the bidding link is an opaque id plus its host. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  getPublicDiscoveryLotByRef: dr({
    method: 'GET', path: '/discovery/public/lots/ref/:shortId', request: NoBody, response: PublicDiscoveryLot, access: 'public',
    summary: 'One public lot by its short id: the address in /lots/{slug}-{short-id}/, where the slug is wording that may have changed since (the page then redirects to the current slug). 404 for anything that is not public, and for a malformed or unknown handle. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  getPublicDiscoveryFacets: dr({
    method: 'GET', path: '/discovery/public/facets', request: NoBody, response: PublicDiscoveryFacets, access: 'public',
    summary: 'The places and categories one brand\'s directory can be browsed by, with how many public companies and upcoming auctions each holds, most companies first. Lets a page decline to exist for an empty combination. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),
  getPublicDiscoverySitemap: dr({
    method: 'GET', path: '/discovery/public/sitemap', request: NoBody, response: PublicDiscoverySitemap, access: 'public',
    summary: 'The feed a sitemap is built from: slug, short id (auctions and lots) and last-changed time of every public auction, company or lot of one brand that earns a page of its own (a place, and words or a way onward; for a lot, a way onward and two details), up to 5000 a page, in slug order. Anonymous; absent unless DISCOVERY_PUBLIC_ENABLED.',
  }),

  // Cut 3: the click report. The redirect itself is DISCOVERY_OUT_PATH, not a
  // route here: its answer is a 302, not a shape.
  discoveryClickReport: dr({
    method: 'GET', path: '/discovery/report/clicks', request: NoBody, response: DiscoveryClickReport, access: 'operator',
    summary: 'Handoffs through /out/:linkId in a window (default the last 30 days, at most 366 days): the total, the first-party share, by brand surface, by purpose, by UTC day, and the twenty most-sent-to companies and auctions. Counts only; the record holds no visitor data to report. Query: from, to, brand, companyId. Operator only.',
  }),
  discoveryTrafficReport: dr({
    method: 'GET', path: '/discovery/report/traffic', request: NoBody, response: DiscoveryTrafficReport, access: 'operator',
    summary: 'What the directory pages measured in a window (default the last 30 days, at most 366 days): views, visitors per day, engaged visits and outbound clicks kept as four separate numbers, with search impressions, by brand surface, referrer class, page type, UTC day, campaign, category, state and discovery source, and the twenty busiest companies and auctions. Counts only; nothing about a person is recorded. Query: from, to, brand, companyId. Operator only.',
  }),
  subscribeToDiscoveryAlerts: dr({
    method: 'POST', path: '/discovery/alerts/subscribe', request: DiscoveryAlertSubscribeBody, response: DiscoveryAlertSubscribeResult, access: 'visitor',
    summary: 'Ask for one brand\'s auction alerts (double opt-in): records a PENDING subscription, and the worker then emails a confirmation link. The answer is the same for a new, pending, active or suppressed address. 422 consent_version_mismatch when the form showed old wording; 503 alerts_unavailable when the brand is not offered. Anonymous, never cached, rate-limited; absent unless DISCOVERY_ALERTS_ENABLED.',
  }),
  confirmDiscoveryAlerts: dr({
    method: 'POST', path: '/discovery/alerts/confirm', request: DiscoveryAlertTokenBody, response: DiscoveryAlertConfirmResult, access: 'visitor',
    summary: 'Follow the confirmation link: PENDING becomes ACTIVE and the link stops working. One 400 invalid_token for every failure (malformed, forged, used, expired, withdrawn). A POST, so a mail scanner that fetches the link cannot confirm for the person. Anonymous; absent unless DISCOVERY_ALERTS_ENABLED.',
  }),
  unsubscribeFromDiscoveryAlerts: dr({
    method: 'POST', path: '/discovery/alerts/unsubscribe', request: DiscoveryAlertTokenBody, response: DiscoveryAlertUnsubscribeResult, access: 'visitor',
    summary: 'Unsubscribe one brand\'s alerts with the manage token from any email. Idempotent. Touches that brand only. Anonymous; absent unless DISCOVERY_ALERTS_ENABLED.',
  }),
  updateDiscoveryAlertPreferences: dr({
    method: 'POST', path: '/discovery/alerts/preferences', request: DiscoveryAlertPreferencesBody, response: DiscoveryAlertPreferences, access: 'visitor',
    summary: 'Read or change an ACTIVE subscription\'s preferences with the manage token: first name, ZIP, radius, categories, frequency. Only the fields sent change; null clears. 409 not_active for any other state. Anonymous; absent unless DISCOVERY_ALERTS_ENABLED.',
  }),
  discoverySubscriberReport: dr({
    method: 'GET', path: '/discovery/report/subscribers', request: NoBody, response: DiscoverySubscriberReport, access: 'operator',
    summary: 'Alert subscriptions by brand and state now, and what happened in a window (default the last 30 days, at most 366 days) by brand and kind. Counts only: no address, name or ZIP. Operator only.',
  }),
  suppressDiscoverySubscriber: dr({
    method: 'POST', path: '/discovery/subscribers/suppress', request: SuppressDiscoverySubscriberBody, response: SuppressDiscoverySubscriberResult, access: 'operator',
    summary: 'Put one brand\'s address on the do-not-mail list (SUPPRESSED), creating the entry when the address was never subscribed. Nothing the visitor does can lift it. Audited as discovery.subscriber.suppress with the subscription id and the reason, never the address. Operator only.',
  }),

  exportDiscoverySubscriber: dr({
    method: 'POST', path: '/discovery/subscribers/export', request: ExportDiscoverySubscriberBody, response: ExportDiscoverySubscriberResult, access: 'operator',
    summary: 'Everything the directory holds about one address on one brand\'s list (subject access): the subscription row and its consent events. Found false when there is nothing. A POST so the address stays out of every access log. Audited as discovery.subscriber.export with the subscription id and the reason, never the address. Operator only.',
  }),
  eraseDiscoverySubscriber: dr({
    method: 'POST', path: '/discovery/subscribers/erase', request: EraseDiscoverySubscriberBody, response: EraseDiscoverySubscriberResult, access: 'operator',
    summary: 'Erase one address from one brand\'s list on the person\'s request: the name, ZIP, radius, categories and the address itself are removed and the row becomes a do-not-mail tombstone nobody can read. A row already on the do-not-mail list keeps its address (so nobody mails it) and loses everything else. Audited as discovery.subscriber.erase with the subscription id and the reason, never the address. Operator only.',
  }),

  // Cut 6c: the image desk and the SuperAdmin takedown. Operator only, never behind a switch: an operator
  // can always see the images and always take one down.
  discoveryImageSummary: dr({
    method: 'GET', path: '/discovery/images/summary', request: NoBody, response: DiscoveryImageSummary, access: 'operator',
    summary: 'The image desk at a glance: images by status, how many content-hash and address blocks stand, and takedown cleanups still pending or failed. Counts only. Operator only.',
  }),
  listDiscoveryImages: dr({
    method: 'GET', path: '/discovery/images', request: NoBody, response: DiscoveryImageList, access: 'operator',
    summary: 'Every image the directory holds or has been told of, with its source, storage state and what a page would show of it now. Filters: status, sourceKey, companyId, auctionId. Newest first. Paged: check the remaining count. Operator only.',
  }),
  takedownDiscoveryImages: dr({
    method: 'POST', path: '/discovery/images/takedown', request: DiscoveryTakedownBody, response: DiscoveryTakedownResult, access: 'operator',
    summary: 'Take down one image, every image of an auction or of a company, or every image of a source (which also becomes TAKEDOWN_BLOCKED). The images become REMOVED for good, their addresses (and, unless blockContent is false, their bytes) are blocked from every source and both brands, and the worker deletes the stored objects and purges the CDN. Needs the INC or CHG number of the record that asked for it; dryRun counts and writes nothing. Operator only, audited as discovery.image.takedown.',
  }),

  // Cut 7: agents. Operator only, never behind a switch (the ingest door has its own, DISCOVERY_AGENTS_ENABLED).
  listDiscoveryAgents: dr({
    method: 'GET', path: '/discovery/agents', request: NoBody, response: z.array(DiscoveryAgent), access: 'operator',
    summary: 'Every discovery agent with its source, state, limits and when it last submitted. Never returns a credential. Operator only.',
  }),
  createDiscoveryAgent: dr({
    method: 'POST', path: '/discovery/agents', request: CreateDiscoveryAgentBody, response: DiscoveryAgentCreated, access: 'operator',
    summary: 'Issue an agent: a credential that can submit observations to one PERMITTED source and do nothing else. The credential is in this answer and nowhere else, ever (only its hash is kept). autoPublish defaults to false: what the agent sends waits in PENDING_REVIEW. Operator only, audited as discovery.agent.create.',
  }),
  updateDiscoveryAgent: dr({
    method: 'PATCH', path: '/discovery/agents/:agentKey', request: UpdateDiscoveryAgentBody, response: DiscoveryAgent, access: 'operator',
    summary: 'Change an agent\'s name, autoPublish, submissions a minute or observations a submission. A revoked agent cannot be changed. Operator only, audited as discovery.agent.update.',
  }),
  revokeDiscoveryAgent: dr({
    method: 'POST', path: '/discovery/agents/:agentKey/revoke', request: RevokeDiscoveryAgentBody, response: DiscoveryAgent, access: 'operator',
    summary: 'Revoke an agent for good: its credential stops working at once and the key is never reused. Records it already submitted stay as they are. Operator only, audited as discovery.agent.revoke.',
  }),
  discoveryAgentReport: dr({
    method: 'GET', path: '/discovery/report/agents', request: NoBody, response: DiscoveryAgentReport, access: 'operator',
    summary: 'Per agent over a window (default the last 30 days, at most 366 days): runs, observations, accepted, duplicates, rejected and why, published, what the agent said it cost, and how long the backend took. Counts only. Operator only.',
  }),

  // Cut 8a: lots. Operator only, never behind a switch: an operator can always see lots and always hide one.
  listDiscoveryLots: dr({
    method: 'GET', path: '/discovery/lots', request: NoBody, response: DiscoveryLotList, access: 'operator',
    summary: 'The review desk list of directory lots, with provenance and their bidding link. Filters: status, auctionId, companyId, search. Newest first. Paged: check `remaining`. Operator only.',
  }),
  getDiscoveryLot: dr({
    method: 'GET', path: '/discovery/lots/:ref', request: NoBody, response: DiscoveryLot, access: 'operator',
    summary: 'One directory lot in full, by short id or id, with its sources and its bidding link. Operator only.',
  }),
  setDiscoveryLotStatus: dr({
    method: 'POST', path: '/discovery/lots/:id/status', request: SetDiscoveryLotStatusBody, response: DiscoveryLot, access: 'operator',
    summary: 'Move a lot between PENDING_REVIEW, PUBLISHED, HIDDEN and RETRACTED along DISCOVERY_LOT_TRANSITIONS. RETRACTED is terminal. PUBLISHED also needs its auction PUBLISHED and its sources still PERMITTED. Operator only, audited as discovery.lot.status.',
  }),
} as const;

export type DiscoveryRoutes = typeof DISCOVERY_ROUTES;
export type DiscoveryRouteName = keyof DiscoveryRoutes;
